// API Service for Orella Patient App
// Connects to live backend at api.orellahealth.com
import { authGet, authSet, authRemove } from '../utils/authStorage';
import { INSURANCE_DISCOVERY_ENABLED } from '../config/featureFlags';
import { uploadRefusalFromResponse, uploadFailureOf } from '../utils/uploadRefusal';
import { rememberEmergencyTrip } from '../utils/emergencyTrips';
import { extractCardBody } from '../utils/payerPick';
import { withoutProfilePhone } from '../utils/profileWire';
import { signalAiConsentRequired } from '../utils/aiConsentSignal';
import { AI_DISCLOSURE_VERSION } from '../utils/aiDisclosure';

export const API_BASE = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? 'https://api.orellahealth.com/api/v1' : 'http://localhost:8000/api/v1');

// ★★★ Build 35 — EIGHT CALL SITES SPELLED THIS `API_URL`, A NAME BOUND NOWHERE IN THIS
//   FILE, since 2026-06-27. The whole medication ledger (list / add / update /
//   discontinue / remove) and the insurance carrier and IPA lookups threw
//   `ReferenceError: Can't find variable: API_URL` on first call, in every build for two
//   months. Found by `scripts/check-free-identifiers.mjs` while gating the Deliver-screen
//   crash — a second live defect of the same class, in a different file.
//   ★ The env var IS called `VITE_API_URL`; the binding is `API_BASE`. Two names one
//     concept apart, which is the shape §1az/§1bd keeps recording.
const TOKEN_KEY = 'orella_patient_token';

// ★★★ v242.87.0 (TF b34 #13/#17) — was sessionStorage, labelled "HIPAA".
//   In a WKWebView that does not mean "until the app closes"; it means "until
//   this content process goes away", and iOS discards content processes when
//   the app hands control to the share sheet and comes back. The bundle
//   reloaded, the token was gone, ProtectedRoute bounced to /welcome, and two
//   separate TestFlight reports described it as the button navigating home.
//   See src/utils/authStorage.js for why native storage is not a weaker
//   posture — and note that useCaseStore already persists the patient's CASES
//   to localStorage, so auth was held to a stricter standard than the clinical
//   data it protects. Web is unchanged.
export const getToken = () => authGet(TOKEN_KEY);
export const setToken = (token) => authSet(TOKEN_KEY, token);
export const clearToken = () => authRemove(TOKEN_KEY);

// ★★★ v242.131.0 — THE SCOPED 2FA-SETUP BEARER (API v243.139.0).
// /auth/2fa/setup and /auth/2fa/verify-setup used to take the account id out of
// the request body with no auth at all: setup handed the caller a fresh TOTP
// secret and cleared totp_method, and verify-setup answered a code derived from
// that secret with a full session token. A UUID was enough to take over an
// account. The server now issues a short-lived, purpose-scoped token at the three
// points where it has already verified a credential — email verification
// completed, or password accepted — and accepts only that or a real session.
// This is where the client keeps it between those two calls.
const SETUP_TOKEN_KEY = 'orella_2fa_setup_token';
export const getSetupToken = () => authGet(SETUP_TOKEN_KEY);
export const setSetupToken = (t) => authSet(SETUP_TOKEN_KEY, t);
export const clearSetupToken = () => authRemove(SETUP_TOKEN_KEY);

// ★★★ v242.169.0 — TOKEN-KEY-1. A LEGACY KEY NOTHING READ AND NOTHING CLEARED.
// Two screens (TwoFactorSetup, OnboardingSecurity) wrote the LIVE session JWT a
// second time, with a raw localStorage.setItem, under this key. Nothing in the
// app ever read it back — the real token is written by setToken() above, inside
// verify2faSetup — and no logout path removed it, so a valid bearer for the
// account sat in localStorage after Sign Out, on web, where sessionStorage is
// the deliberate policy for exactly that reason. The writes are gone as of this
// version; a device that already ran an earlier build still carries the value,
// which is what this exists to remove.
//
// ★ IT IS DECLARED AS A `*_TOKEN_KEY` ON PURPOSE. check-auth-storage derives its
//   auth-key vocabulary from these declarations, so naming it here makes any
//   future direct storage call on it a red build — the instance is fixed by the
//   declaration, and the class by that gate's ARM 3.
const LEGACY_TOKEN_KEY = 'orella_token';

/**
 * Remove credentials written by builds before v242.169.0. authRemove clears BOTH
 * web stores, so this is complete regardless of which policy wrote the value.
 * Called on boot and on every logout; safe to call any number of times.
 */
export const purgeLegacyAuth = () => {
  try { authRemove(LEGACY_TOKEN_KEY); } catch (_) {}
};

// v242.51.0 (PEN Tier-1): server-side logout — revoke the current token
// (bumps token_version, invalidating every session) BEFORE clearing it locally.
// Fire-and-forget with keepalive so it survives the state teardown that follows,
// and a network failure never traps the user in a signed-in UI.
export const serverLogout = () => {
  try {
    const token = getToken();
    if (token) {
      fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) {}
};

// ★★★ v242.147.0 MEDAPI-FETCHWITHAUTH-1 — THE FUNCTION FIVE CALLERS ASSUMED.
//   `medicationsAPI` (list / add / update / discontinue / remove) has called
//   `fetchWithAuth` since 2026-06-27 and it was never defined, in this file or
//   anywhere else. Every one of those methods threw `ReferenceError` on its
//   first line, so the medication ledger has never been reachable from this app:
//   the list call threw, the screen fell back to the profile mirror, and every
//   add, edit, stop and remove was local-only.
//
//   ★★ IT MUST RETURN A RESPONSE. The callers check `res.ok` and call
//   `res.json()` themselves. `apiFetch` below does the opposite — it parses,
//   throws on a non-2xx, and takes a RELATIVE endpoint — so it cannot stand in
//   for this. Two contracts, deliberately kept apart.
async function fetchWithAuth(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

// Base fetch wrapper with auth
async function apiFetch(endpoint, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Add timeout — configurable per call, default 30s
  const controller = new AbortController();
  const timeoutMs = options.timeout || 30000;
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  // PROD-1115: a caller's `signal` (a list that is closed or replaced by a newer search) also
  // aborts the request. The request's own signal below stays the one carrying the timeout.
  const callerSignal = options.signal;
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort();
    else callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal,
    });
    
    clearTimeout(timeoutId);
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { message: text };
    }
    
    // v242.131.0: capture the scoped 2FA-setup bearer wherever the server issues
    // one. It arrives on a 200 (signup verification) AND on a 403 (login saying
    // needs2faSetup), so this sits ABOVE the ok check deliberately — one capture
    // point instead of one per mint site.
    if (data && data.setupToken) {
      try { setSetupToken(data.setupToken); } catch (_) {}
    }

    if (!response.ok) {
      // PROD-1225: the server's AI consent gate (403 + code 'ai_consent_required') opens the
      // re-consent screen through ONE window event (utils/aiConsentSignal.js). Any other 403
      // dispatches nothing. The error is thrown exactly as before either way.
      signalAiConsentRequired(response.status, data);
      // Provide more specific error messages
      if (response.status === 413) {
        throw { status: 413, message: 'Request too large. Please use smaller images.' };
      }
      throw { status: response.status, ...data };
    }
    
    return data;
  } catch (err) {
    clearTimeout(timeoutId);
    
    // Handle abort/timeout
    if (err.name === 'AbortError') {
      throw { status: 408, message: 'Request timed out. Please try again.' };
    }
    
    // Handle network errors (fail to fetch)
    if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
      console.error('[API] Network error:', err);
      throw { status: 0, message: 'Network error. Please check your connection or try with smaller files.' };
    }
    
    throw err;
  }
}

// GW-CEILING-MARGINAL-3 (2026-08-21) - runDraftLane, ported from the MD panel.
//
// WHY THIS EXISTS HERE NOW. API Gateway kills any request at 30,000 ms and the
// limit is not raisable. Three weeks of [AI_CALL] on the three generators below:
//   handoff_summary     28,632 / 14,842 / 27,513 ms weekly max
//   adaptive_questions  27,108 / 25,239 / 27,284
//   bridge_questions    22,650 / 23,950 / 23,331
// No upward trend and flat inputs, so these do not DRIFT over the line - but the
// worst observed call left 1,368 ms of margin, and a gateway kill does not read
// as a timeout in the browser: it reads as a CORS error, because a gateway-made
// response carries none of the app's headers. That cost a full day of the wrong
// investigation on the MD panel side.
//
// The per-call `timeout:` values these callers passed (120s, 60s, 60s) never
// meant anything: the gateway hung up at 30 s regardless. They are gone.
//
// THE COST, STATED. The server answers `ready` on the first POST when a draft for
// this exact payload already exists, so a repeat is as fast as before. A FIRST
// call that the route could have served instantly from its own database cache now
// pays FIRST_POLL_MS before the client sees it. That is why the first poll here is
// 1,200 ms and not the MD panel's 8,000: these screens are in front of a patient.
async function runDraftLane(endpoint, body, artefact, opts = {}) {
  const first = await apiFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify({ ...body, async: true }),
  });
  if (first?.ready && first?.result) return first.result;
  const key = first?.draftKey;
  if (!key) {
    // An older API, or a shape we do not know: honour the old contract rather
    // than inventing a failure. This is what makes the client safe to ship
    // before, or without, the matching API.
    if (first && typeof first === 'object' && !('ready' in first)) return first;
    throw { status: 502, message: `${artefact}: no draft key returned` };
  }
  const FIRST_POLL_MS = opts.firstPollMs || 1200;
  const POLL_EVERY_MS = opts.pollEveryMs || 2000;
  const DEADLINE_MS = opts.deadlineMs || 120000;
  const started = Date.now();
  await new Promise((r) => setTimeout(r, FIRST_POLL_MS));
  while (Date.now() - started < DEADLINE_MS) {
    let poll = null;
    try {
      poll = await apiFetch(`/ai/draft?caseId=${encodeURIComponent(body.caseId)}` +
        `&artefact=${encodeURIComponent(artefact)}&key=${encodeURIComponent(key)}`);
    } catch (e) {
      // A transient poll failure is not a generation failure. The deadline is the
      // verdict, not any single 5xx.
      console.warn(`[AI] ${artefact} poll failed (will retry):`, e?.message || e);
    }
    if (poll?.ready && poll?.result) {
      console.log(`[AI] ${artefact} ready after ${Math.round((Date.now() - started) / 1000)}s`);
      return poll.result;
    }
    // The third outcome. A lookup has three - found, not yet, CANNOT BE PRODUCED -
    // and a loop that renders two of them makes every real failure cost the whole
    // deadline. Thrown OUTSIDE the try above: that try tolerates transport blips.
    if (poll?.error) throw { status: 502, message: `${artefact}: ${poll.error}` };
    await new Promise((r) => setTimeout(r, POLL_EVERY_MS));
  }
  throw { status: 408, message: `${artefact}: not ready after ${DEADLINE_MS / 1000}s` };
}

// Auth API
// ★★★ THE SERVER'S OWN SENTENCE FOR "this patient never set a session PIN", COPIED
//   ON PURPOSE AND HELD BY A GATE. orella-api's `verify_patient_session_pin` answers
//   HTTP 400 {'valid': False, 'error': 'Session PIN not configured'} when
//   session_pin_enabled is false or session_pin_hash is empty. The session PIN is
//   OPTIONAL — Settings shows it as "Active" or "Off" — so this is the ONE 400 on that
//   route that is NOT a failed verification, and telling a patient "Verification failed"
//   for it is a lie about their own PIN.
//   ★★ A copy of a string is the same class of artefact as the bug it is meant to
//   prevent, so it is NOT trusted on its own: scripts/check-hipaa-pin-route.mjs arm HP-6
//   reads the literal out of orella-api's git blob and compares it byte for byte. If the
//   server ever rewords it, the gate reddens here rather than the modal silently falling
//   back to the generic failure.
//   ★ '/auth/verify-session-pin' also answers 400 with 'Invalid PIN format', which IS a
//   failed verification — which is why callers must match the SENTENCE, not the status.
export const SESSION_PIN_NOT_CONFIGURED = 'Session PIN not configured'

export const authAPI = {
  register: async (email, password, firstName, lastName, phone = null, smsOptIn = false, turnstileToken = null) => {
    const response = await apiFetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
        firstName,
        lastName,
        phone,
        smsOptIn,
        turnstileToken,
      }),
    });
    
    // Don't set token here - user needs to verify first
    return response;
  },
  
  // v242.6.0 (C/#11): body is an object — { pendingToken, channel, code } for the
  // deferred phone-then-email flow, or legacy { userId, code }.
  verify: async (body) => {
    const response = await apiFetch('/auth/verify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return response;
  },
  
  resendCode: async (body) => {
    const payload = typeof body === 'string' ? { userId: body } : body;
    const response = await apiFetch('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response;
  },
  
  // v145.0.0: Two-factor authentication
  setup2fa: async (userId, method, currentCode) => {
    // v242.131.0: `setupToken` is the credential; `userId` is sent only so the
    // server can REFUSE a mismatch. The server never trusts it to name the
    // account — see _resolve_2fa_subject in the API.
    // PROD-1154: while 2FA is ON, the FIRST setup call of a change carries `currentCode`
    //   (utils/currentFactor.js); the setupToken it returns carries that proof, so later calls
    //   in the same change (a method switch, an SMS resend) send none.
    const response = await apiFetch('/auth/2fa/setup', {
      method: 'POST',
      body: JSON.stringify({ userId, method, setupToken: getSetupToken() || undefined, currentCode: currentCode || undefined }),
    });
    return response;
  },
  
  verify2faSetup: async (userId, code, method) => {
    const response = await apiFetch('/auth/2fa/verify-setup', {
      method: 'POST',
      body: JSON.stringify({ userId, code, method, setupToken: getSetupToken() || undefined }),
    });
    if (response.token) {
      setToken(response.token);
      // Setup is complete and a real session exists — the scoped bearer has no
      // further purpose and must not outlive its window.
      clearSetupToken();
    }
    return response;
  },
  
  send2faSms: async (email, password) => {
    const response = await apiFetch('/auth/2fa/send-sms', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return response;
  },
  
  backupCodeLogin: async (email, password, backupCode) => {
    const response = await apiFetch('/auth/2fa/backup-login', {
      method: 'POST',
      body: JSON.stringify({ email, password, backupCode }),
    });
    if (response.token) {
      setToken(response.token);
    }
    return response;
  },
  
  // PROD-1271: patient 2FA self-reset by e-mail link. Both routes are PUBLIC; the server
  //   answers the unknown-path 404 (HTML, no code) while its switch
  //   PATIENT_2FA_EMAIL_RESET_ENABLED is OFF, and apiFetch throws { status: 404 } for it.
  /**
   * Ask for a 2FA reset link by e-mail, from the 2FA step of the e-mail/password sign-in.
   *
   * ★★★ THE BODY IS EXACTLY { email, password }: the two values the sign-in step already holds
   *   in its own state, the same shape send2faSms and backupCodeLogin send. The password is the
   *   proof that the password step passed; nothing new is stored anywhere.
   * ★★ THE 200 CARRIES NO INFORMATION and the caller must not look for any: the server answers
   *   one sentence for a mailed link, an unknown address, a wrong password, a cooldown and every
   *   other case. So this returns nothing.
   */
  request2faEmailReset: async (email, password) => {
    await apiFetch('/auth/2fa/email-reset/request', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  /**
   * Spend a 2FA reset link. ONLY the button on /two-factor-reset calls this: opening the page
   * must send nothing (mail scanners prefetch every link). The token is the credential: it is
   * never logged, never stored, never put in an error string. No session comes back.
   */
  confirm2faEmailReset: async (token) => {
    await apiFetch('/auth/2fa/email-reset/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  },

  // v158.1.0: Social login (Google / Apple SSO)
  socialLogin: async (provider, token, firstName, lastName) => {
    const body = { provider, token }
    if (firstName) body.firstName = firstName
    if (lastName) body.lastName = lastName
    const response = await apiFetch('/auth/social-login', {
      method: 'POST',
      body: JSON.stringify(body),
    })
    if (response.token) {
      setToken(response.token)
    }
    return response
  },

  // s95: the second step of an SSO sign-in whose account has 2FA. Carries the server's
  // short-lived challenge, never the provider token again. `secret` is { code } or
  // { backupCode }.
  socialLoginChallenge: async (ssoChallenge, secret) => {
    const response = await apiFetch('/auth/social-login', {
      method: 'POST',
      body: JSON.stringify({ ssoChallenge, ...(secret || {}) }),
    })
    if (response.token) {
      setToken(response.token)
    }
    return response
  },

  // s95: verify a mobile for an account that already exists (SSO sign-ups have none).
  // The server takes the account from the session or the scoped 2FA-setup bearer.
  // s95b: send-code carries the Cloudflare Turnstile token (same bot control as the email
  //   sign-up); verify carries the notification-SMS choice as a real boolean.
  phoneSendCode: async (phone, turnstileToken) => apiFetch('/auth/phone/send-code', {
    method: 'POST',
    body: JSON.stringify({ phone, turnstileToken: turnstileToken || undefined, setupToken: getSetupToken() || undefined }),
  }),
  // s95d: the one front door — does a live account hold this email, and how does it sign in?
  emailCheck: async (email, turnstileToken) => apiFetch('/auth/email-check', {
    method: 'POST',
    body: JSON.stringify({ email, turnstileToken: turnstileToken || undefined }),
  }),
  // PROD-1154: an SMS-2FA account moving its sign-in number also sends `currentCode` (a code to the CURRENT
  //   number, or a backup code) once the API has asked for it (403 2fa_current_factor_required).
  phoneVerify: async (phone, code, smsOptIn, currentCode) => apiFetch('/auth/phone/verify', {
    method: 'POST',
    body: JSON.stringify({ phone, code, smsOptIn: smsOptIn === true, setupToken: getSetupToken() || undefined, currentCode: currentCode || undefined }),
  }),

  // ★★ TEST 3.01 (API lane/a301): change the account e-mail / mobile from Settings, proven on the NEW value.
  //   `reauth` is { currentPassword } or { reauthProvider, reauthToken } (utils/contactChange reauthFields, the
  //   Delete Account fields), forwarded as given. The swap bumps token_version, so the OLD session is dead the moment
  //   verify answers: the fresh `token` replaces the stored one HERE, before any caller can send another request.
  //   These four never send the scoped 2FA-setup bearer (Settings has a session; a setup bearer gets no new session).
  emailChangeCode: async (email, reauth) => apiFetch('/auth/contact/email/code', {
    method: 'POST',
    body: JSON.stringify({ email, ...(reauth || {}) }),
  }),
  emailChangeVerify: async (changeToken, code) => {
    const response = await apiFetch('/auth/contact/email/verify', {
      method: 'POST',
      body: JSON.stringify({ changeToken, code }),
    })
    if (response && response.token) setToken(response.token)
    return response
  },
  // A REPLACEMENT carries `reauth` and answers { changeToken }; a first number sends none (as phoneSendCode).
  contactPhoneSendCode: async (phone, turnstileToken, reauth) => apiFetch('/auth/phone/send-code', {
    method: 'POST',
    body: JSON.stringify({ phone, turnstileToken: turnstileToken || undefined, ...(reauth || {}) }),
  }),
  contactPhoneVerify: async (phone, code, smsOptIn, currentCode, changeToken) => {
    const response = await apiFetch('/auth/phone/verify', {
      method: 'POST',
      body: JSON.stringify({ phone, code, smsOptIn: smsOptIn === true, currentCode: currentCode || undefined, changeToken: changeToken || undefined }),
    })
    if (response && response.token) setToken(response.token)
    return response
  },

  // s95: record Terms + Privacy acceptance (append-only on the server, hashed text).
  // PROD-763 (S11): `privacy` = privacyConsentClaim(useLegalContent('privacy')), i.e. which privacy
  // text this app showed: {source: 'database'|'bundled', version}. Omitted while unknown.
  recordTerms: async (channel, privacy) => apiFetch('/consents/terms', {
    method: 'POST',
    body: JSON.stringify({
      accepted: true, channel,
      privacySource: privacy?.source || undefined,
      privacyVersion: privacy?.version || undefined,
      setupToken: getSetupToken() || undefined,
    }),
  }),
  
  // PROD-1154: while 2FA is ON the reset needs a current code too (or the f2 grant of a backup-code sign-in).
  reset2fa: async (currentCode) => {
    const response = await apiFetch('/auth/2fa/reset', {
      method: 'POST',
      body: JSON.stringify({ currentCode: currentCode || undefined, setupToken: getSetupToken() || undefined }),
    });
    // PROD-1381: with the server switch ON a 2FA change signs out every other session and answers a fresh
    // `token` for this device; store it or this device is signed out on its next request. No token = unchanged.
    if (response && response.token) setToken(response.token);
    return response;
  },

  get2faStatus: async () => {
    return apiFetch('/auth/2fa/status');
  },
  
  // PROD-1154: new backup codes need a CURRENT code (utils/currentFactor.js); a backup code given here is spent.
  regenerateBackupCodes: async (currentCode) => {
    const response = await apiFetch('/auth/2fa/regenerate-codes', {
      method: 'POST',
      body: JSON.stringify({ currentCode: currentCode || undefined }),
    });
    // PROD-1381: same fresh `token` as reset2fa; store it through setToken(). No token = unchanged.
    if (response && response.token) setToken(response.token);
    return response;
  },

  // PROD-1154: text a CURRENT sign-in code to the signed-in SMS account (for the field above). No body.
  sendCurrent2faCode: async () => {
    return apiFetch('/auth/2fa/send-current-code', {
      method: 'POST',
      body: '{}',
    });
  },
  
  login: async (email, password, pin, code) => {
    const body = { email, password };
    if (pin) body.pin = pin;
    if (code) body.code = code;
    const response = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    
    if (response.token) {
      setToken(response.token);
    }
    
    return response;
  },
  
  logout: () => {
    serverLogout();
    clearToken();
    clearSetupToken();
  },

  // v242.5.0: patient-initiated account deletion (App Store 5.1.1(v) + HIPAA).
  // ★★★ v242.134.0 — THIS WRAPPER ATE THE RE-AUTHENTICATION.
  //     v242.90.0 added a fresh-provider sign-in to the delete flow and
  //     v243.104.0 (D146) made it MANDATORY for SSO-only accounts. This
  //     signature predates both: it destructured only password/reason/confirm and
  //     re-serialised only those three, so `reauthProvider` and `reauthToken` were
  //     DROPPED HERE, silently, after the patient had completed a real Google
  //     sign-in. The server then correctly answered `reauth_required` — every
  //     time, forever — and the screen said "Sign in again ... then try once
  //     more." Signing in again could not possibly help; the token never left the
  //     device. SSO-ONLY ACCOUNT DELETION WAS IMPOSSIBLE, which is an App Store
  //     5.1.1(v) obligation as well as a defect.
  // ★★★ §1cf(a), THE HOUSE DEFECT: an answer computed and then discarded — and
  //     the answer here cost the patient an entire interactive re-authentication.
  // ★★ A wrapper that re-serialises a FIXED key list silently narrows every
  //    caller that grows. Forward what you were given.
  deleteAccount: async ({ password = '', reason = '', confirm = false,
                          reauthProvider, reauthToken } = {}) => {
    const response = await apiFetch('/account/delete', {
      method: 'POST',
      body: JSON.stringify({ password, reason, confirm, reauthProvider, reauthToken }),
    });
    clearToken();
    clearSetupToken();
    return response;
  },
  
  // Get current user data (refreshes user info including orellaMemberId)
  getMe: async () => {
    const response = await apiFetch('/auth/me', {
      method: 'GET',
    });
    return response;
  },

  // v237.6.0: Session PIN (lock-on-return). Distinct from the legacy phone-verify PIN.
  setSessionPin: async (pin) => {
    return apiFetch('/auth/session-pin', {
      method: 'PUT',
      body: JSON.stringify({ pin }),
    });
  },

  verifySessionPin: async (pin) => {
    return apiFetch('/auth/verify-session-pin', {
      method: 'POST',
      body: JSON.stringify({ pin }),
    });
  },

  updateSessionPinSettings: async (settings) => {
    return apiFetch('/auth/session-pin-settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  },

  // v238.21.0: Change password (verifies current, stores new PBKDF2 hash server-side)
  changePassword: async (oldPassword, newPassword) => {
    const response = await apiFetch('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ oldPassword, newPassword }),
    });
    // PROD-1349: the API ends every OTHER session with the change and answers with a fresh token for this
    // device; store it through setToken() or this device would be signed out on its next request.
    if (response && response.token) setToken(response.token);
    return response;
  },
  
  // Forgot password flow
  forgotPassword: async (phone) => {
    const response = await apiFetch('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ phone }),
    });
    return response;
  },
  
  verifyResetCode: async (userId, code, resetToken, phone) => {
    const response = await apiFetch('/auth/verify-reset-code', {
      method: 'POST',
      body: JSON.stringify({ userId, code, resetToken, phone }),
    });
    return response;
  },
  
  resetPassword: async (userId, password, resetAuthToken) => {
    const response = await apiFetch('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ userId, password, resetAuthToken }),
    });
    return response;
  },
  
  resendResetCode: async (userId) => {
    const response = await apiFetch('/auth/resend-reset-code', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
    return response;
  },

  // ═══ THE E-MAIL LINK RESET — the SECOND recovery channel, API v243.x ═══════
  //
  // ★★★ WHY THIS EXISTS BESIDE THE FOUR CALLS ABOVE AND NOT INSTEAD OF THEM. The
  //   SMS flow above is the only way back into an account today, and it needs a
  //   telephone number on the row. Most patient rows carry none, so those people
  //   cannot even START a reset; and a number that appears on more than one row
  //   now deliberately sends nothing at all. The e-mail channel is how those
  //   people get back in. Removing the SMS screens is a separate decision that
  //   ships on its own date — NOT here.
  //
  // ★★ THE PATHS ARE /patients/…, NOT /auth/…, AND THAT IS NOT A TYPO. The API
  //   put the e-mail pair under the patient prefix to mirror the physician pair
  //   (/doctors/forgot-password, /doctors/reset-password) and to keep them OUT of
  //   the /auth/ block the SMS flow lives in, because that block is scheduled to
  //   be deleted and a route that outlives a block must not sit inside it.
  //   Both are PUBLIC and unauthenticated: the caller is by definition somebody
  //   who cannot sign in. apiFetch attaches a bearer if one happens to be in
  //   storage; the server ignores it on these two routes.

  /**
   * Ask for a password-reset link by e-mail.
   *
   * ★★★ THE RESPONSE CARRIES NO INFORMATION AND THE CALLER MUST NOT LOOK FOR ANY.
   *   The server answers ONE 200 and ONE fixed sentence for a registered address,
   *   an unknown one, a deleted account, an unverified stub, an address inside its
   *   cooldown, and a send that failed. That is deliberate: "is this person an
   *   Orella patient" is a fact about a named individual's care and is answerable
   *   to anybody, one request at a time. Reading ANY field of the 200 body to
   *   decide what to render rebuilds the oracle inside the success case.
   *   So this returns nothing. There is nothing to return.
   *
   * ★ The two statuses that DO differ are facts about the request's characters
   *   (400, not an address at all) and about our own health (503, the lookup
   *   failed) — identical for a known and an unknown address, so neither
   *   distinguishes one from the other. apiFetch throws on both.
   */
  forgotPasswordEmail: async (email) => {
    await apiFetch('/patients/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Redeem a reset link and set a new password.
   *
   * ★★★ THIS IS THE ONLY THING THAT SPENDS THE TOKEN, AND IT MUST STAY THAT WAY.
   *   Hospital and corporate mail filters PREFETCH every link in a delivered
   *   message. If opening the page spent the token, the scanner would win the race
   *   against the patient and they would be handed "invalid link" for a mail they
   *   had just received. A scanner cannot POST a new password, so the click must
   *   open a PAGE and this call must be reachable only from a submitted form.
   *   DO NOT CALL THIS ON MOUNT, AND DO NOT PREFETCH IT.
   *
   * ★★ THE TOKEN IS THE CREDENTIAL. It is never logged, never put in a console
   *   line, and never sent anywhere but here.
   *
   * ★ Unknown, malformed, expired, already-spent, deleted-account and
   *   lost-the-race all answer with ONE sentence and a 400. The caller must not
   *   try to tell them apart — it cannot, and the person holding a dead link needs
   *   a way forward rather than a diagnosis.
   */
  resetPasswordByLink: async (token, password) => {
    await apiFetch('/patients/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },
  
  // Verify PIN for current user (used for sensitive operations)
  //
  // ★★★ RETIRED 2026-09-22 (TEST 1.20). THIS CALLED A ROUTE THAT HAS NEVER EXISTED.
  //   It POSTed to '/auth/verify-pin'. In orella-api,
  //   `/usr/bin/grep -c 'verify-pin' api/index.py` is 0, against 25 registered
  //   '/api/v1/auth/' routes in the same file; production answered the POST with 404
  //   on 2026-09-22 while '/auth/forgot-password' answered 400 from the same client.
  //   The three PIN routes that DO exist are '/auth/session-pin' (PUT),
  //   '/auth/verify-session-pin' (POST) and '/auth/session-pin-settings' (PUT).
  //
  //   ★★ SO THE HIPAA NOTICE ANSWERED "Verification failed" TO THE CORRECT PIN, for
  //   every patient, and the data export and the medical-note download sat behind it.
  //   One of those is a right of access. Before the accept-on-error catch below was
  //   removed the gate ALWAYS opened (a plain 500 opened it with 0000); afterwards it
  //   could NEVER open. It was never once correct.
  //
  //   ★★★ NOT A SILENT STUB, DELIBERATELY. Returning a rejection object here would let
  //   the next caller re-adopt a dead endpoint and read the refusal as "wrong PIN".
  //   It throws with the reason and the replacement named. The PIN a patient actually
  //   sets is the SESSION PIN — call `verifySessionPin` above, which is the one caller
  //   of '/auth/verify-session-pin' and stays the only one.
  //
  //   ★ HELD BY scripts/check-hipaa-pin-route.mjs, whose arm HP-2 fails if ANY endpoint
  //   this file sends is not a registered route in orella-api's own git blob.
  verifyPin: async () => {
    throw new Error(
      'authAPI.verifyPin is retired: POST /auth/verify-pin is not a route in orella-api ' +
      '(404 in production, 0 occurrences in api/index.py). Use authAPI.verifySessionPin, ' +
      'which calls the session-PIN route the patient actually sets.')
  },
  
  // Verify an invitation code from MD-created patient
  verifyInvite: async (inviteCode) => {
    try {
      const response = await apiFetch(`/auth/verify-invite/${inviteCode}`, {
        method: 'GET',
      });
      return response;
    } catch (err) {
      if (err.status === 404) {
        return { valid: false, error: 'Invitation not found' };
      }
      if (err.status === 410) {
        return { valid: false, expired: true, error: 'Invitation has expired' };
      }
      throw err;
    }
  },
  
  // Get case preview for invite page (public endpoint - no auth required)
  getCasePreview: async (caseId) => {
    try {
      const response = await apiFetch(`/cases/${caseId}/preview`, {
        method: 'GET',
      });
      return response;
    } catch (err) {
      console.log('[API] getCasePreview error:', err);
      return null;
    }
  },
  
  // Complete registration for MD-created patient using invite code
  registerWithInvite: async (inviteCode, password, pin) => {
    const response = await apiFetch('/auth/register-invite', {
      method: 'POST',
      body: JSON.stringify({
        inviteCode,
        password,
        pin,
      }),
    });
    
    if (response.token) {
      setToken(response.token);
    }
    
    return response;
  },
};

// Cases API
export const casesAPI = {
  // s119 (D531, API v243.394.0): someone opened this case's share link; the patient allows or denies them.
  //   -> { request: {id, clinicianTitle, specialty, practice, city, stateCode, npi, role, staffName, staffTitle,
  //   requestedAt, expiresAt, viewMinutes} | null, last: the latest request in any state | null }
  getOpenRequest: async (caseId) => apiFetch(`/cases/${encodeURIComponent(caseId)}/open-request`),
  decideOpenRequest: async (caseId, requestId, decision) => apiFetch(`/cases/${encodeURIComponent(caseId)}/open-request/decision`, {
    method: 'POST',
    body: JSON.stringify({ requestId, decision }),
  }),
  // ★★★ v242.145.0 PVS-FACESHEET-CLIENT-SOURCE-1 — WHO THE PATIENT IS, ASKED
  //     RATHER THAN REMEMBERED. The pre-visit hand-off used to build its
  //     facesheet from useProfileStore, which is sessionStorage-backed and is
  //     empty after the WKWebView content-process recycle the SHARE SHEET
  //     itself triggers (utils/authStorage.js says so in writing). The document
  //     then printed "Sex: Not provided" four inches under the AI's
  //     "42-year-old male" — because the API had the profile all along and the
  //     client did not. This route is lean on purpose: no AI, no encounters,
  //     no case body. Just the person.
  getFacesheet: async (caseId) => {
    return apiFetch(`/cases/${caseId}/facesheet`);
  },

  // R08 · PROD-212 (P2-1): the wallet pass routes admit only the case's own patient — by bearer,
  // or by a short-lived signed link this route mints (orella-api D5c S9, v243.284.0). Answers
  // { applePath, googlePath, expiresIn }: PATHS under /api/v1, which the caller prefixes with the
  // API origin (utils/walletPass.js walletLinkUrl). Native iOS opens the pass URL without a bearer.
  walletLink: async (caseId) => {
    return apiFetch(`/cases/${caseId}/wallet/link`, { method: 'POST' });
  },

  // R20 / PROD-163 (patient P1): `safety.urgentScreening` is the emergency screening with every
  // answer change (utils/urgentScreening.js buildUrgentScreening). orella-api D5b create_case
  // validates it and stores it ONCE on the case; it is sent only when present.
  // R21 / PROD-164 (patient P1, Alex Q46): `safety.emergencyTrips` are the 911 trips the symptom
  // router answered before this case existed (utils/emergencyTrips.js); sent only when non-empty.
  create: async (specialty, focusArea, safety = {}) => {
    const body = { specialty: specialty, focusArea: focusArea };
    if (safety && safety.urgentScreening) body.urgentScreening = safety.urgentScreening;
    if (safety && Array.isArray(safety.emergencyTrips) && safety.emergencyTrips.length > 0) body.emergencyTrips = safety.emergencyTrips;
    return apiFetch('/cases', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  
  // Sync a local case to the database (creates if not exists, updates if exists)
  syncCase: async (caseData) => {
    // PROD-903: the id only — caseData is the whole local case (answers, transcript, appointment rows).
    console.log('syncCase sending, case:', caseData?.id ?? null);
    // ★ PROD-1224: POST /cases/sync (sync_case) answers { case: row } and apiFetch hands the body back
    //   unchanged, so ShareScreen's `syncedCase?.id` read undefined and send-to-doctor kept the local
    //   id. Unwrapped ONCE here, tolerant of a bare row too. Gate: scripts/check-sync-case-shape.mjs.
    const body = await apiFetch('/cases/sync', {
      method: 'POST',
      body: JSON.stringify({
        specialty: caseData.specialty,
        focusArea: caseData.focusArea,
        voiceTranscript: caseData.voiceTranscript,
        voiceTextInput: caseData.voiceTextInput,
        voiceRecordingUrl: caseData.voiceRecordingUrl,
        voiceDuration: caseData.voiceDuration,
        questionnaireResponses: caseData.questionnaireResponses,
        describeSummary: caseData.describeSummary,
        photos: caseData.photos,
        documents: caseData.documents,
      }),
    });
    return body && typeof body === 'object' && 'case' in body ? body.case : body;
  },
  
  list: async () => {
    return apiFetch('/cases');
  },
  
  // ★ PROD-918: GET /cases/<id> (get_case) answers { case: row } and apiFetch hands the body back
  //   unchanged, so five callers that read the row's own fields read undefined. Unwrapped ONCE
  //   here: every caller gets the row. Idempotent (a bare row passes through), so the
  //   `x.case || x` readers keep working. Gate: scripts/check-case-get-shape.mjs.
  get: async (caseId) => {
    const body = await apiFetch(`/cases/${caseId}`);
    return body?.case ?? body;
  },
  
  update: async (caseId, updates) => {
    return apiFetch(`/cases/${caseId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  },

  // v242.235.0 (session 96) — the "View my summary" tap: answers become read-only and the
  //   summary, once written, is final (orella-api v243.279.0). Returns { lockedAt, summaryKept }.
  // A note from the patient to their physician — how a correction is made once answers are
  //   locked. Returns { notes }.
  addPatientNote: async (caseId, text) => apiFetch(`/cases/${caseId}/patient-notes`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  }),

  // v230.3.1 — Validated authorization save. The server enforces the claims
  // gate (number + non-expired expiry + non-zero visits) and returns 422 with
  // { reasonCode, reason } on reject. On success it files the doc + extracted
  // fields under the authorization header and sets appointmentRequest.authNumber.
  savePatientAuthorization: async (caseId, payload) => {
    return apiFetch(`/cases/${caseId}/patient-authorization`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // v121.6.1: Dedicated reopen endpoint — bypasses protected_statuses guard
  // ★★ P7 A8/P1 (TEST 2.10; Alex Q6, Q7) — `notes` (≤1,000 chars) and `photoKeys` (≤5
  //   storage paths from POST /cases/<id>/upload-url) are forwarded, not narrowed away.
  //   Both are optional; omitting them keeps today's request byte-for-byte.
  reopen: async (caseId, { requestedDate, requestedTime, visitType, requestedBy = 'patient', notes, photoKeys }) => {
    const payload = { requestedDate, requestedTime, visitType, requestedBy }
    if (notes !== undefined) payload.notes = notes
    if (photoKeys !== undefined) payload.photoKeys = photoKeys
    return apiFetch(`/cases/${caseId}/reopen`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  
  // ★★★ P2 Release 3 (PROD-167, REP-08) — NO BASE64 FALLBACK. This helper, casesAPI
  //   .uploadPhoto and profileAPI.uploadPhoto each POSTed /files/upload on their own and
  //   answered ANY failure (a 403 storage_path_denied included) by reading the blob as a
  //   data: URL and returning it as `audio_url` / `photo_url` — base64 PHI that a caller
  //   would have written into the row as if it were a stored reference. They now go
  //   through filesAPI.upload and let its UploadRefusedError (status + code) propagate.
  //   0 callers today (measured 2026-09-26; scripts/check-p2-access.mjs UP11 counts them).
  uploadVoice: async (caseId, audioBlob) => {
    const timestamp = Date.now();
    const ext = audioBlob.type?.includes('webm') ? 'webm' : audioBlob.type?.includes('mp4') ? 'mp4' : 'webm';
    const storagePath = `cases/${caseId}/voice/${timestamp}_recording.${ext}`;
    const file = new File([audioBlob], `recording.${ext}`, { type: audioBlob.type || 'audio/webm' });
    const result = await filesAPI.upload(file, storagePath);
    return { audio_url: result.path, storage_path: result.path, signed_url: result.url };
  },
  
  // P2 Release 3: no base64 fallback — see uploadVoice above.
  uploadPhoto: async (caseId, photoBlob) => {
    const timestamp = Date.now();
    const ext = photoBlob.type?.includes('png') ? 'png' : 'jpg';
    const storagePath = `cases/${caseId}/photos/${timestamp}_photo.${ext}`;
    const file = new File([photoBlob], `photo.${ext}`, { type: photoBlob.type || 'image/jpeg' });
    const result = await filesAPI.upload(file, storagePath);
    return { photo_url: result.path, storage_path: result.path, signed_url: result.url };
  },
  
  // ★★ L10 A7 (TEST 1.08; Alex L10 D1 = Track A) — an optional trailing OPTIONS object,
  //   `{ renew }`. The five positional arguments are UNCHANGED (every existing caller is
  //   untouched). `renew:true` asks the API to extend the SAME code and PIN in place
  //   (200 `{ shareCode (unchanged), pin (unchanged), expiresAt, renewed:true }`) instead
  //   of minting a new one; a case that cannot be renewed (claimed, sent, no code, a
  //   revoke sentinel, or a terminal status) answers 409 `share_code_not_renewable`. An
  //   API that predates L10 does not recognise `renew` and mints as it always did — its
  //   response carries no `renewed` field at all. Gate: scripts/check-share-renew.mjs.
  generateShareCode: async (caseId, requirePin = true, pharmacy = null, agreement = null, doctorSource = null, options = null) => {
    const payload = { requirePin }
    if (pharmacy) {
      payload.pharmacy = pharmacy
    }
    if (agreement) {
      payload.agreement = agreement
    }
    if (doctorSource) {
      payload.doctorSource = doctorSource
    }
    if (options && options.renew) {
      payload.renew = true
    }
    console.log('[API] Generating share code with:', { requirePin, hasPharmacy: !!pharmacy, hasAgreement: !!agreement, doctorSource, renew: !!(options && options.renew) })
    return apiFetch(`/cases/${caseId}/share`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  
  // Revoke a share code — makes it inaccessible
  // ★ s98 Phases 4 + 7 (D468) — the server's Case Report PDF (one PDF for view, share and
  //   fax; his ruling 2026-10-07 #1) and the patient's own faxes of it. reportPdf returns the
  //   raw Response (binary); utils/caseReport.js turns it into a blob for saveOrShareBlob.
  reportPdf: async (caseId) => fetchWithAuth(`${API_BASE}/cases/${caseId}/report.pdf`, { method: 'GET' }),
  faxSend: async (caseId, body) => apiFetch(`/cases/${caseId}/fax-sends`, {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  faxSends: async (caseId) => apiFetch(`/cases/${caseId}/fax-sends`),

  revokeShareCode: async (caseId) => {
    console.log('[API] Revoking share code for case:', caseId)
    return apiFetch(`/cases/${caseId}/revoke-share`, {
      method: 'POST',
    });
  },

  // v229.5.0: Email the patient's doctor an invite to claim the case on Orella
  // (external/outside-MD path conversion hook). Returns { success }.
  remindDoctor: async (caseId, email) => {
    return apiFetch(`/remind-doctor`, {
      method: 'POST',
      body: JSON.stringify({ caseId, email }),
    });
  },

  // v199.2: Complete onboarding — creates appointment row, checks conflicts, Zoom, notifications
  // v213.4 (C-A-198): 90s timeout — payload includes signature image + detect file metadata.
  completeOnboarding: async (caseId, data) => {
    console.log('[API] Complete onboarding for case:', caseId, 'keys:', Object.keys(data))
    return apiFetch(`/cases/${caseId}/complete-onboarding`, {
      method: 'POST',
      body: JSON.stringify(data),
      timeout: 90000,
    });
  },

  // v175.6.0: Patient pre-visit check-in
  checkIn: async (caseId, checkInData) => {
    console.log('[API] Patient check-in for case:', caseId)
    return apiFetch(`/cases/${caseId}/check-in`, {
      method: 'POST',
      body: JSON.stringify(checkInData),
    });
  },
  
  // Send case directly to an Orella-registered doctor
  // v213.4 (C-A-198): 90s timeout — payload includes signature image + detect file metadata.
  // MEET PA0: `recordingConsentFields` is bookingConsentFields() (src/utils/videoVisit.js),
  //   { recordingConsent, consentVersion } or {}; the API reads both at the top of the body.
  sendToDoctor: async (caseId, doctorId, appointmentRequest = null, preferredPharmacy = null, patientAgreement = null, recordingConsentFields = null) => {
    // MEET PA4 (R5 info 5): presence only — the booking body, pharmacy and agreement never reach the console.
    console.log('[API] Sending case to doctor:', caseId, 'appointment:', !!appointmentRequest, 'pharmacy:', !!preferredPharmacy, 'agreement:', !!patientAgreement);
    const payload = { doctorId };
    if (appointmentRequest) {
      payload.appointmentRequest = appointmentRequest;
      if (recordingConsentFields) Object.assign(payload, recordingConsentFields);
    }
    if (preferredPharmacy) {
      payload.preferredPharmacy = preferredPharmacy;
    }
    if (patientAgreement) {
      payload.patientAgreement = patientAgreement;
    }
    const response = await apiFetch(`/cases/${caseId}/send-to-doctor`, {
      method: 'POST',
      body: JSON.stringify(payload),
      timeout: 90000,
    });
    // MEET PA5: presence only — the response carries the booked row (a Meet row holds meeting_url).
    console.log('[API] Send to doctor response received, appointment:', !!response?.appointment);
    return response;
  },
  
  // Delete a case
  delete: async (caseId) => {
    console.log('[casesAPI.delete] Deleting case:', caseId);
    const response = await apiFetch(`/cases/${caseId}`, {
      method: 'DELETE',
    });
    console.log('[casesAPI.delete] Response:', !!response?.success); // PROD-903: no whole answer
    return response;
  },
  
  // Archive a case with reason
  archive: async (caseId, reason) => {
    try {
      return await apiFetch(`/cases/${caseId}/archive`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    } catch (error) {
      // ★★ NO FABRICATED SUCCESS. This used to return { success: true } on any
      //   failure, so the API layer told the store the server had archived the
      //   case when it had not. archiveCase() catches and logs the real error.
      console.error('[casesAPI.archive] archive failed for', caseId, error);
      throw error;
    }
  },

  // v237.20.0: Reactivate an archived case. Detaches from the prior doctor and
  // resets to a pre-share state so the patient must share again.
  reactivate: async (caseId) => {
    return await apiFetch(`/cases/${caseId}/reactivate`, {
      method: 'POST',
    });
  },

  // Generate AI visit recap from AVS (v120.4.5)
  getVisitRecap: async (caseId) => {
    try {
      return await apiFetch(`/cases/${caseId}/visit-recap`, {
        method: 'POST',
      });
    } catch (error) {
      console.log('[casesAPI.getVisitRecap] Error:', error);
      return { recap: null, error: error.message };
    }
  },

  // v240.0.0 (REF-AK): createReferralCase wrapper removed — POST /cases/<id>/refer
  // is retired (410). Referral creation is single-path + gated (MD-side POST /referrals).

  // v222.0.0: Stedi eligibility check (Seam 1 — fires 270/271 against payer)
  // Returns { eligibility: {...last_eligibility shape...}, error?: string }
  // Endpoint short-circuits if last check was <1hr ago for same doctor NPI,
  // unless skipCache=true.
  // s105 (TestFlight b71): `doctorId` = the doctor being booked, for a case that names no doctor yet
  //   (the share step). The API uses it only then, only for the case's own patient and a bookable doctor.
  checkEligibility: async (caseId, { serviceTypeCode = '30', skipCache = false, doctorId = null } = {}) => {
    return apiFetch(`/cases/${caseId}/eligibility-check`, {
      method: 'POST',
      body: JSON.stringify({ service_type_code: serviceTypeCode, skip_cache: skipCache, ...(doctorId ? { doctor_id: doctorId } : {}) }),
      timeout: 35000, // Stedi responses can take 10-25s; pad timeout
    });
  },
};

// Questionnaires API
export const questionnairesAPI = {
  getAll: async () => {
    return apiFetch('/questionnaires');
  },
  
};

// Profile API
// v242.43.0 — AI data-use consent (App Store 5.1.1(i)/5.1.2(i)). status() drives
// whether the consent step / AI features are unblocked; record() is called on
// affirmative opt-in in the onboarding "AI & your data" step; withdraw() backs
// the Profile → AI & Data control.
export const aiConsentAPI = {
  status() {
    return apiFetch('/ai-consent/status');
  },
  // PROD-1225 (review M3): send the version of the text the patient saw, so the ledger row is true.
  // The API records it when it is the server's version or the next one; an older one answers 409
  // {code: 'ai_consent_update_required', error: <update sentence>, currentVersion} and writes nothing;
  // a malformed one or one more than a version ahead answers 400 {code: 'ai_consent_version_invalid'}.
  record() {
    return apiFetch('/ai-consent', { method: 'POST', body: JSON.stringify({ version: AI_DISCLOSURE_VERSION }) });
  },
  withdraw() {
    return apiFetch('/ai-consent/withdraw', { method: 'POST', body: JSON.stringify({}) });
  },
};

export const profileAPI = {
  get: async () => {
    const response = await apiFetch('/profile');
    return response.profile;
  },
  
  update: async (profileData) => {
    const response = await apiFetch('/profile', {
      method: 'PUT',
      body: JSON.stringify(withoutProfilePhone(profileData)),
    });
    return response.profile;
  },

  // TEST 1.02 R2 — the same PUT, but it also says whether the server REFUSED an ID card in
  // it (the name on the card is not the account's). A separate method so that every other
  // caller keeps receiving exactly the profile it always did.
  updateReportingRefusal: async (profileData) => {
    const response = await apiFetch('/profile', {
      method: 'PUT',
      body: JSON.stringify(withoutProfilePhone(profileData)),
    });
    return { profile: response.profile, idDocumentRefused: !!response.idDocumentRefused };
  },

  // v222.1.0: Stedi insurance discovery (Seam 2)
  // Fires on onboarding completion to find coverage from patient demographics.
  // Returns { discovery: {...discovery_result shape...}, error?: string, searchesLeft?: number }.
  // ★ U2-INS (TEST 3.02 / 3.07): every search is PAID, and the server holds 3 per insurance.
  //   `target` is 'primary' or 'secondary' (each has its own 3); `reason` is 'search' (the
  //   Search button: unchanged details answer error 'same_inputs' for free) or 'recheck'
  //   (Home's 90-day re-check: it runs only while 2 of 3 remain). Both are sent only when
  //   given, so an older call keeps its exact body. utils/discoverySearch.js reads the answer.
  runInsuranceDiscovery: async (userId, { skipCache = false, caseId = null, target = null, reason = null } = {}) => {
    // s110: discovery is turned off (config/featureFlags.js INSURANCE_DISCOVERY_ENABLED); no request is made.
    if (!INSURANCE_DISCOVERY_ENABLED) return { discovery: null, error: 'discovery_disabled' };
    return apiFetch(`/users/${userId}/insurance-discovery`, {
      method: 'POST',
      body: JSON.stringify({ skip_cache: skipCache, case_id: caseId, ...(target ? { target } : {}), ...(reason ? { reason } : {}) }),
      timeout: 45000, // Discovery probes multiple payers — can take 20-35s
    });
  },

  // v227.13.0 Stedi (Seam 1, pre-doctor): run a doctor-independent eligibility check
  // against the Orella practice NPI to get a plan-level benefits snapshot, persisted to
  // profiles.insurance.last_eligibility. Soft-fails (eligibility:null) for self-pay / no
  // member ID. Returns { eligibility: {...last_eligibility shape...}, error?: string }.
  runPracticeEligibility: async (userId, { serviceTypeCode = '30' } = {}) => {
    return apiFetch(`/users/${userId}/eligibility`, {
      method: 'POST',
      body: JSON.stringify({ service_type_code: serviceTypeCode }),
      timeout: 45000,
    });
  },

  // v238.9.0 (S17-5): Medicare MBI lookup. The SSN is sent over TLS to build the
  // Stedi request and is used transiently only — never stored or logged. On a
  // match the server saves the MBI to insurance.medicare and returns it here.
  // Returns { mbi: { mbi, found, enrollment_pending, coverage }, error? }.
  lookupMbi: async (userId, { ssn } = {}) => {
    return apiFetch(`/users/${userId}/mbi-lookup`, {
      method: 'POST',
      body: JSON.stringify(ssn ? { ssn } : {}),
      timeout: 45000,
    });
  },

  // v238.9.0 (S17-4): register/update the patient's SECONDARY policy (COB). Pass
  // null to clear. Server stamps the Stedi payer ID and fires a silent secondary
  // eligibility check. Returns { secondary, eligibility }.
  saveSecondaryInsurance: async (userId, secondary) => {
    return apiFetch(`/users/${userId}/secondary-insurance`, {
      method: 'POST',
      body: JSON.stringify({ secondary: secondary || null }),
      timeout: 45000,
    });
  },

  // P2 Release 3: no base64 fallback — see casesAPI.uploadVoice. The API rewrites a
  // patient's profiles/ key to profiles/<her id>/..., and `path` is that stored key.
  uploadPhoto: async (photoBlob) => {
    const timestamp = Date.now();
    const ext = photoBlob.type?.includes('png') ? 'png' : 'jpg';
    const storagePath = `profiles/${timestamp}_photo.${ext}`;
    const file = new File([photoBlob], `photo.${ext}`, { type: photoBlob.type || 'image/jpeg' });
    const result = await filesAPI.upload(file, storagePath);
    return { photo_url: result.path, storage_path: result.path, signed_url: result.url };
  },
  
  // Parse medical document using AI to extract health information
  parseMedicalDocument: async ({ file, fileType, fileName }) => {
    try {
      const response = await apiFetch('/ai/parse-medical-document', {
        method: 'POST',
        body: JSON.stringify({
          file, // base64 encoded — sent to Claude for AI parsing, not stored
          fileType,
          fileName
        }),
      });
      return response;
    } catch (error) {
      console.error('Failed to parse medical document:', error);
      // ★★★ THIS CATCH USED TO RETURN FABRICATED CLINICAL DATA. On a 404 or any
      //   'fetch' error it returned success:true with a Penicillin allergy, a
      //   Sulfa allergy ("difficulty breathing"), Lisinopril / Metformin /
      //   Atorvastatin, Type 2 Diabetes, Hypertension, an appendectomy, a family
      //   history of breast cancer and a social history — none of it the
      //   patient's. Every caller checks `success && data` and applies it:
      //   OnboardingAutoFill calls applyAutofillResult() and advances, so a NEW
      //   patient's first profile could be built entirely out of it, and the Case
      //   Report their doctor reads is built from that profile.
      //   ★★ All three callers already handle a throw with a real message. A
      //   document we could not read is not a document that said this.
      throw error;
    }
  },
};

// Appointments API
export const appointmentsAPI = {
  getAll: async () => {
    const response = await apiFetch('/appointments');
    return response.appointments || [];
  },
  
  create: async (appointmentData) => {
    const response = await apiFetch('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
    return response;
  },
  
  update: async (appointmentId, updateData) => {
    const response = await apiFetch(`/appointments/${appointmentId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
    return response;
  },
  
  delete: async (appointmentId) => {
    const response = await apiFetch(`/appointments/${appointmentId}`, {
      method: 'DELETE',
    });
    return response;
  },

  // MEET PA1 (MEET_PLAN 2c, API S5/M6): the patient's recording decision before an in-app join.
  //   body { decision: 'accepted' | 'declined', consentVersion }. Throws on any refusal (409 for
  //   a Zoom, in-person or cancelled row) or network error; JoinVideoVisit then opens nothing.
  recordingConsent: async (appointmentId, { decision, consentVersion }) => {
    return apiFetch(`/appointments/${encodeURIComponent(appointmentId)}/recording-consent`, {
      method: 'POST',
      body: JSON.stringify({ decision, consentVersion }),
    });
  },

  // s114 video sign-on (canvas V2/V3): the physician's license states + consent version, then the sign-on record
  //   { identityMethod, locationState, consentVersion, consentAgreed } (utils/videoSignOn signOnBody). Throws on refusal
  //   (409 location_not_licensed carries licensedStates).
  videoSignOnInfo: async (appointmentId) => {
    return apiFetch(`/appointments/${encodeURIComponent(appointmentId)}/video-sign-on`, { method: 'GET' });
  },
  videoSignOn: async (appointmentId, body) => {
    return apiFetch(`/appointments/${encodeURIComponent(appointmentId)}/video-sign-on`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};

// AI API - for patient-facing AI features
// ─────────────────────────────────────────────────────────────────────────────
// v242.203.0 (TEST 1.14) — SSE STREAM DIAGNOSIS. Read this before changing the
// streaming summary, because the obvious change is the wrong one.
//
// ★★★ THE FAILURE IS NOT IN THIS FILE AND MUST NOT BE "FIXED" HERE. Measured
//    2026-09-21 from Alex's console during the dry run: every generation logged
//    `[ViewSummary] Starting SSE stream...`, then `[SSE] Stream error:` WITH NOTHING
//    AFTER THE COLON, then the fallback. Two of two attempts. The cause is the
//    transport, and it is already written down in three independent places:
//      · orella-api serves Flask through `Mangum(WsgiToAsgi(app))` (api/index.py at
//        origin/main, `_http_handler = Mangum(WsgiToAsgi(app), lifespan="off")`).
//        Mangum returns ONE BUFFERED PAYLOAD: the generator is drained to completion
//        before a single byte leaves the Lambda. HTTP APIs do not carry Lambda
//        response streaming. So this endpoint CANNOT stream, however correct both
//        ends are.
//      · A cold `patient_summary` takes ~81 s (orella-api's own SUMMARY-GW-TIMEOUT-1
//        block, measured on a real device 2026-07-31: three consecutive 503s, then an
//        85 ms cache hit once the abandoned work had landed).
//      · API Gateway's integration timeout is 30,000 ms and is NOT raisable on an
//        HTTP API. At 30 s the gateway kills the request and answers with a
//        GATEWAY-MADE response — which carries none of this app's CORS headers.
//    ★★ AND THAT LAST STEP IS WHY THE REASON IS EMPTY, and this repository already
//      knew it: read the GW-CEILING-MARGINAL-3 comment above `runDraftLane`, ~180
//      lines up — "a gateway kill does not read as a timeout in the browser: it reads
//      as a CORS error, because a gateway-made response carries none of the app's
//      headers. That cost a full day of the wrong investigation on the MD panel side."
//      A cross-origin failure REJECTS the fetch. `response` never exists, so the
//      `!response.ok` branch below — the only one that reports a status — is never
//      reached, and the browser deliberately withholds the reason from JavaScript.
//
// ★★★ THE PROOF IS IN ALEX'S OWN LOG, BESIDE THE FAILURE. The same session logged
//    `[AI] handoff_summary ready after 10s`. That line is printed by `runDraftLane`.
//    Five of this app's six AI generators — summary-reading-level, avs-reading-level,
//    handoff-summary, adaptive-questions, bridge-questions — were moved onto that
//    job+poll lane in GW-CEILING-MARGINAL-3 for exactly this reason. Same device, same
//    gateway, same token, same minute: the lane succeeded and the synchronous stream
//    failed. `/ai/patient-summary-stream` is THE ONE GENERATOR LEFT ON THE SYNCHRONOUS
//    GATEWAY PATH. That is a fact about the route, not about the network.
//
// ★★★ SO WHAT THIS CODE DOES IS REPORT, NOT REPAIR. Building a client workaround here
//    would hide a transport defect the API side has to act on. The client's own half of
//    TEST 1.14 is that the failure was UNDIAGNOSABLE, and that half is real:
//      · `console.error('[SSE] Stream error:', err)` printed the raw exception. For an
//        error whose `.message` is empty — which is what several engines produce for a
//        cross-origin rejection and for a connection closed mid-body — that prints a
//        label and nothing else. MEASURED, with a control: an error carrying a real
//        message prints it, so an empty line is a fact about the error, not the log.
//      · `callbacks.onError?.({ error: err.message })` forwarded `.message` AND NOTHING
//        ELSE — dropping `err.name`, the one field that is always populated, and with it
//        the status, the content-type, the elapsed time, the bytes received and the
//        number of SSE frames parsed. ViewSummary could not have said why either.
//      · Nothing counted. A fallback that fires on 100% of attempts is not a fallback,
//        it is the only path, and the client could not say so.
//    Every one of those is measured below and printed in ONE line, and `reason` can no
//    longer be empty: it degrades `.message` -> `.name` -> the phase it died in.
//
// ★★ `chunks` IS THE FIELD THAT MATTERS AND IT IS WHY IT IS COUNTED. A buffered
//    response is indistinguishable from a streamed one by its content — the events are
//    the same bytes in the same order. It is distinguishable by ARRIVAL: a real stream
//    delivers many reader chunks over time, a buffered payload delivers one. So the
//    client can now state, from its own measurement and without a network panel,
//    whether this response was streamed. That is the single fact the API side needs and
//    it is the one nobody had.
//
// ★★★ AND THE "FALLBACK" IS NOT AN INDEPENDENT PATH, WHICH IS WHY THE FEATURE LOOKS
//    LIKE IT WORKS. ViewSummary falls back to `generatePatientSummary`, which is
//    `apiFetch('/ai/patient-summary', { timeout: 120000 })` — the SAME synchronous
//    gateway, and that 120,000 ms is one of the "timeouts written in the client" the
//    GW-CEILING-MARGINAL-3 comment calls a wish: the gateway hangs up at 30 s whatever
//    the client asks for. The second request succeeds only because the FIRST one's
//    Lambda ran on to completion after the gateway hung up and wrote `patient_summary`,
//    so the fallback is served from cache. orella-api measured exactly this shape on
//    2026-07-31: three consecutive 503s, then an 85 ms cache hit once the abandoned
//    work had landed. So the ten seconds the patient spends at a blank screen is the
//    server finishing the request that was already reported as failed — and if the
//    generation ever takes longer than the fallback's own 30 s window, BOTH die and the
//    patient gets `setGenFailed`. Neither request is independent of the other.
//
// ★★ WHAT THE API SIDE CAN ACT ON WITHOUT RE-MEASURING ANY OF THIS:
//    /ai/patient-summary-stream is the ONLY AI generator in this client still on the
//    synchronous gateway path. The remedy already exists in this repository and is
//    already deployed for the other five: the `/ai/draft` job+poll lane that
//    `runDraftLane` drives. An /ai/patient-summary-stream that answered `{ draftKey }`
//    and let the client poll would need no new infrastructure and no streaming
//    support. Deleting the streamed path instead is also a defensible answer — the row
//    says so — but it is Alex's decision, not this file's.
//
// ★ NOTHING HERE REACHES THE PATIENT. The patient needs the summary and gets it, from
//   the same fallback as before; a developer needs the reason and now has it in the
//   console. Two audiences, two surfaces. No new user-visible string, and therefore
//   none of the ELEVEN locale files is touched.
// ─────────────────────────────────────────────────────────────────────────────
let _sseAttempts = 0        // times the streamed path was started
let _sseProgressive = 0     // ...of which actually arrived in more than one chunk

export const aiAPI = {
  // TF b42 — the reading-level control on the Diagnose review (AVS-READING-LEVEL-1).
  // Rewrites the doctor's own after-visit summary at a plainer or a more clinical
  // level. Rides runDraftLane because a full-AVS rewrite is a 30-60 s generation and
  // the gateway kills anything at 30,000 ms.
  //
  // ★★ THE ARTEFACT NAME CARRIES THE LEVEL, AND IT HAS TO. cases.ai_drafts holds ONE
  //    draft per artefact name, so a single shared name would make the two levels
  //    evict each other and every toggle back would pay a fresh generation instead of
  //    the cache hit the API answers on the first POST.
  // ★ Returns null on failure rather than throwing. The caller stays on the doctor's
  //   own wording and says so; there is no half-rewritten state to fall into.
  // SUMMARY-READING-LEVEL-1 — re-word the PRE-VISIT summary. Sibling of
  // avsReadingLevel and a separate call on purpose: the AVS is one plain-text document
  // and this is structured data, so the two have different guarantees on the far side.
  //   ★ Returns null on failure. The caller stays on the original wording and says so.
  summaryReadingLevel: async (caseId, level, summaryData) => {
    const artefact = level === 'simpler' ? 'summary_level_simpler' : 'summary_level_detailed';
    try {
      return await runDraftLane('/ai/summary-reading-level',
        { caseId, level, summaryData }, artefact);
    } catch (err) {
      console.warn('[AI] Summary reading level failed:', err?.message || err);
      return null;
    }
  },

  avsReadingLevel: async (caseId, level, avsContent) => {
    const artefact = level === 'simpler' ? 'avs_level_simpler' : 'avs_level_detailed';
    try {
      return await runDraftLane('/ai/avs-reading-level',
        { caseId, level, avsContent }, artefact);
    } catch (err) {
      console.warn('[AI] AVS reading level failed:', err?.message || err);
      return null;
    }
  },

  // Generate AI-powered pre-visit summary (used in Visit Prep PDF)
  generatePreVisitSummary: async (caseId) => {
    try {
      const response = await apiFetch('/ai/pre-visit-summary', {
        method: 'POST',
        body: JSON.stringify({ caseId }),
      });
      return response;
    } catch (err) {
      console.warn('[AI] Pre-visit summary generation failed:', err);
      return null;
    }
  },

  // Generate the clinician hand-off block for the share PDF (lean, single call).
  // Returns the merged pre_visit_summary (with .handoff). 120s timeout — AI is slow.
  generateHandoffSummary: async (caseId, forceRegenerate = false) => {
    try {
      const response = await runDraftLane('/ai/handoff-summary',
        { caseId, forceRegenerate }, 'handoff_summary');
      return response;
    } catch (err) {
      console.warn('[AI] Hand-off summary generation failed:', err);
      return null;
    }
  },

  // v227.10.0: Detect conflicts between the patient's case (voice + answers) and
  // their saved profile, for reconciliation on the Review step. Non-blocking —
  // returns null on failure so the Review screen simply shows no card.
  detectProfileDiscrepancies: async (caseId, forceRegenerate = false) => {
    try {
      const response = await apiFetch('/ai/profile-discrepancies', {
        method: 'POST',
        body: JSON.stringify({ caseId, forceRegenerate }),
        timeout: 60000,
      });
      return response;
    } catch (err) {
      console.warn('[AI] Profile discrepancy detection failed:', err);
      return null;
    }
  },

  // A-30: Analyze external doctor's note — extracts diagnoses, treatments, AVS
  // Can send fileData (base64) directly, or let API fetch from case.external_doctor_note
  analyzeExternalNote: async (caseId, fileData = null) => {
    const payload = { caseId };
    if (fileData && fileData.length > 0) {
      payload.fileData = fileData;
    }
    const response = await apiFetch('/ai/analyze-external-note', {
      method: 'POST',
      body: JSON.stringify(payload),
      timeout: 120000, // 120s — downloading + AI analysis
    });
    return response;
  },

  // v230.3.0 — Classify + extract an uploaded administrative document.
  // documentType 'auto' lets the API decide authorization vs referral and
  // return a `documentType` discriminator plus the extracted fields. Used by
  // the patient Authorizations & Referrals screen so the patient never picks
  // a type. documentUrl is a Supabase Storage URL (the API fetches the bytes).
  extractDocument: async (caseId, documentUrl, documentName, documentType = 'auto') => {
    const response = await apiFetch('/ai/extract-document', {
      method: 'POST',
      body: JSON.stringify({ caseId, documentUrl, documentName, documentType }),
      timeout: 120000, // 120s — fetch + vision extraction
    });
    return response;
  },

  // Get patient-friendly summary for View Summary screen
  // ★ PROD-143b (P5, TEST-adjacent to TEST 1.37/2.06 — plan p6_2026-09-26 §
  //   orella-patient-app P5). This was the ONE remaining AI generator still on
  //   the synchronous gateway path (see the giant comment above
  //   `streamPatientSummary`, ~150 lines up): a cold generation is measured at
  //   64-81s, and API Gateway kills any request at a non-raisable 30,000 ms —
  //   so a genuinely cold call could only ever succeed by accident, on a
  //   retry that landed after an earlier abandoned Lambda had already written
  //   the cache. `runDraftLane` is the same job+poll lane the other five AI
  //   generators already ride, with a 120s poll deadline that actually means
  //   something because polling, unlike the synchronous path, is never itself
  //   subject to the 30s ceiling.
  // ★ Degrades safely on an API that has not yet added 'patient_summary' to
  //   its async route table (API R1 not live): `runDraftLane`'s first POST
  //   carries `async: true`, which that route ignores — it answers
  //   synchronously, exactly as before, and `runDraftLane`'s own fallback
  //   ("an older API, or a shape we do not know") returns that answer
  //   unchanged, since it has no `ready` key. No client nonce — a forced
  //   request's dedupe-breaking nonce is generated server-side (API R1, A3).
  generatePatientSummary: async (caseId, forceRegenerate = false, language = 'en') => {
    return runDraftLane('/ai/patient-summary',
      { caseId, forceRegenerate, language }, 'patient_summary');
  },
  
  // v161.0.0: Generate adaptive follow-up questions from Stage 1 answers
  // Uses Sonnet for clinical quality. Returns 3-5 targeted questions.
  generateAdaptiveQuestions: async (caseId, language = 'en') => {
    const response = await runDraftLane('/ai/adaptive-questions',
      { caseId, language }, 'adaptive_questions');
    return response;
  },

  // v219.5.0: Sonnet-backed survey auto-completion from the voice transcript.
  // Replaces the client-side regex matcher (utils/voicePrefill.js). Sends the
  // questionnaire the client already has loaded; the server reads the full
  // transcript against it and returns high-confidence answers keyed by
  // `{category}_{questionId}` (same shape the client merges into case.answers).
  // Swallows errors (returns null) so DescribeVoice can silently fall back to
  // the legacy regex prefill — this must NEVER block the intake flow.
  extractSurveyAnswers: async (caseId, questionsByCategory, language = 'en') => {
    try {
      const response = await apiFetch('/ai/extract-survey-answers', {
        method: 'POST',
        body: JSON.stringify({ caseId, questionsByCategory, language }),
        timeout: 60000, // 60s — Sonnet call over full transcript + questionnaire
      });
      return response; // { answers, allAnswers, confidence, evidence, notes, ... }
    } catch (err) {
      console.warn('[AI] Survey extraction failed (will fall back to local prefill):', err);
      return null;
    }
  },

  // v218.0.6: Idempotently persist an already-streamed summary to the DB.
  // Covers the gap where the stream's server-side complete-capture didn't land
  // and patient_summary never reached the DB (caused permanent detect-prep 409).
  // Sends the exact streamed result so the patient's reviewed differential is
  // preserved verbatim. Swallows errors (returns null) — never breaks the flow.
  // v242.235.0 — fill ONE empty field ("why this may apply to you", "learn more", sources) of
  //   ONE summary entry, so it reads the same on every open. Best-effort; never replaces.
  enrichSummary: async (caseId, { kind, name, field, value }) => {
    try {
      return await apiFetch(`/cases/${caseId}/patient-summary/enrich`, {
        method: 'POST',
        body: JSON.stringify({ kind, name, field, value }),
      });
    } catch (err) {
      return null;
    }
  },

  persistSummary: async (caseId, summary) => {
    try {
      const response = await apiFetch('/ai/persist-summary', {
        method: 'POST',
        body: JSON.stringify({ caseId, summary }),
        timeout: 30000,
      });
      return response;
    } catch (err) {
      console.warn('[AI] Persist summary failed:', err);
      return null;
    }
  },

  // v219.4: On-demand 'Learn More' for ONE condition. learnMore is no longer
  // generated in the bulk summary (was 5 essays/case + the AVS latency
  // hold-up); fetched here only when the patient taps Learn More on a card.
  getConditionDetail: async (diagnosis, patientNarrative = '', whyRelevant = '', language = 'en', icd10 = '') => {
    try {
      const response = await apiFetch('/ai/condition-detail', {
        method: 'POST',
        body: JSON.stringify({ diagnosis, patientNarrative, whyRelevant, language, icd10 }),
        timeout: 30000,
      });
      return response; // { diagnosis, learnMore, sources }
    } catch (err) {
      console.warn('[AI] Condition detail failed:', err);
      return null;
    }
  },

  // VIEWSUMMARY-LEAN-1: on-demand "why this matches you" for ONE condition. The
  // bulk differential now emits a 6-10 word hook in patientDescription and no
  // per-condition prose; this fetches the patient-specific paragraph only when a
  // card is actually opened. Sibling of getConditionDetail and deliberately a
  // different call: that one is GENERAL education about the condition and is told
  // not to reference the patient's symptoms, this one exists to do exactly that.
  //
  // Returns null on any failure, like its siblings — and the CALLER shows a retry
  // rather than substituting text. There is deliberately no local template here: a
  // fabricated paragraph would be indistinguishable from a generated one, which is
  // how a transport failure gets read as a design failure.
  // SUMMARY-READING-LEVEL-1 — `level` rides alongside `language` because it is the same
  // kind of thing: a property of the READER, not of the case. Default 'standard' keeps
  // every existing caller on exactly the behaviour it has today.
  getConditionWhy: async (diagnosis, patientNarrative = '', supportingFactors = [], language = 'en', level = 'standard') => {
    try {
      const response = await apiFetch('/ai/condition-why', {
        method: 'POST',
        body: JSON.stringify({ diagnosis, patientNarrative, supportingFactors, language, level }),
        timeout: 30000,
      });
      return response; // { diagnosis, whyRelevant }
    } catch (err) {
      console.warn('[AI] Condition why failed:', err);
      return null;
    }
  },

  // v219.5.7: On-demand expanded explainer for ONE red flag. The bulk summary
  // now emits a single short sentence per flag (scannable cards + shorter
  // stream); this fetches the deeper "why it matters / what to do" paragraph
  // only when the patient taps Learn More on a specific warning sign. Mirrors
  // getConditionDetail. Swallows errors (returns null) — non-blocking.
  getRedFlagDetail: async (finding, patientNarrative = '', relevance = '', urgency = '', language = 'en') => {
    try {
      const response = await apiFetch('/ai/redflag-detail', {
        method: 'POST',
        body: JSON.stringify({ finding, patientNarrative, relevance, urgency, language }),
        timeout: 30000,
      });
      return response; // { finding, detail }
    } catch (err) {
      console.warn('[AI] Red flag detail failed:', err);
      return null;
    }
  },

  // v229.1.0: On-demand 'Learn More' for ONE focus-area concern on the focus
  // page (e.g. Ear → 'Hearing loss'). Short plain-language description so the
  // patient can decide whether it fits before starting a case. Mirrors
  // getConditionDetail / getRedFlagDetail. Swallows errors (returns null).
  getConcernDetail: async (concern, specialty = '', language = 'en') => {
    try {
      const response = await apiFetch('/ai/concern-detail', {
        method: 'POST',
        body: JSON.stringify({ concern, specialty, language }),
        timeout: 30000,
      });
      return response; // { concern, detail }
    } catch (err) {
      console.warn('[AI] Concern detail failed:', err);
      return null;
    }
  },

  // v235.6.0: Home-grid "Learn more" — short plain-language description of a
  // specialty AREA (e.g. "Heart"), so the patient can decide before opening it.
  // Mirrors getConcernDetail: fast Haiku, single paragraph, nothing persisted.
  getAreaDetail: async (area, reviewedBy = '', language = 'en') => {
    try {
      const response = await apiFetch('/ai/area-detail', {
        method: 'POST',
        body: JSON.stringify({ area, reviewedBy, language }),
        timeout: 30000,
      });
      return response; // { area, detail }
    } catch (err) {
      console.warn('[AI] Area detail failed:', err);
      return null;
    }
  },

  // v218.0.2: Detect-prep — builds three-group evidence guidance on the committed
  // differential. Fired fire-and-forget at submit→share so it's ready before
  // Detect mounts; also used as a one-shot fallback if Detect finds it absent.
  // Swallows errors (returns null) — must never break the submit animation.
  generateDetectPrep: async (caseId, language = 'en', forceRegenerate = false) => {
    try {
      const response = await apiFetch('/ai/detect-prep', {
        method: 'POST',
        body: JSON.stringify({ caseId, language, forceRegenerate }),
        timeout: 60000, // 60s — single-purpose Sonnet call
      });
      return response;
    } catch (err) {
      console.warn('[AI] Detect-prep generation failed:', err);
      return null;
    }
  },

  // v213.9 (Sprint 13C-A2): Per-category augmentation for the General specialty intake.
  // Called after each of the 4 anchor categories. Returns 1-3 AI-generated follow-ups
  // whose answers narrow the differential. Only invoked for general specialty cases.
  // Cached server-side per case+category — second call returns cached result.
  augmentCategory: async (caseId, category, language = 'en', priorQuestions = []) => {
    const response = await apiFetch('/ai/augment-category', {
      method: 'POST',
      // v226.10.0: priorQuestions = every augment question the patient has already seen
      // (assembled client-side from sessionStorage). Lets the server dedup independent of
      // DB write-timing, killing cross-category repeats from the augment fire race.
      body: JSON.stringify({ caseId, category, language, priorQuestions }),
      timeout: 45000, // 45s — Sonnet call, smaller token budget than adaptive
    });
    return response;
  },

  // v219.5.6 (Sprint 13C-A): Transcript pre-fill for the General path's
  // AI-generated augmentation questions. The anchors already get voice pre-fill
  // (extractSurveyAnswers is questionnaire-agnostic); this closes the gap where
  // the ~12 AI augment questions got ZERO pre-fill and were hand-answered even
  // when the voice memo already answered them.
  //
  // Sends the resolved augment questions for ONE category; the server runs the
  // SAME, UNMODIFIED Sonnet extractor (same SURVEY_EXTRACTION_PROMPT, same
  // negation/speaker/named-disease safety rules, same HIGH-only gating) over
  // the patient's existing transcript. Returns answers keyed
  // `{category}_augment_{augQid}` — byte-identical to getAugmentAnswerKey, so
  // the client merges them straight into case.answers with no remapping.
  //
  // Swallows errors (returns null) so DescribeSection silently falls back to
  // the patient answering the augment questions themselves — this must NEVER
  // block the intake flow (identical contract to extractSurveyAnswers).
  extractAugmentAnswers: async (caseId, category, questions, language = 'en') => {
    try {
      const response = await apiFetch('/ai/extract-augment-answers', {
        method: 'POST',
        body: JSON.stringify({ caseId, category, questions, language }),
        timeout: 45000, // 45s — Sonnet call over transcript + small augment set
      });
      return response; // { answers, allAnswers, confidence, evidence, ... }
    } catch (err) {
      console.warn('[AI] Augment extraction failed (patient will answer manually):', err);
      return null;
    }
  },
  
  // v173.0.0: Generate AI bridge questions for follow-up/observation check-ins
  // When voiceTranscript is provided, questions are personalized to what the patient said
  generateBridgeQuestions: async (caseId, encounterId, bridgeType = 'follow_up_checkin', language = 'en', voiceTranscript = '') => {
    const response = await runDraftLane('/ai/bridge-questions',
      { caseId, encounterId, bridgeType, language, voiceTranscript }, 'bridge_questions');
    return response;
  },
  
  // v118.2: Stream patient analysis via SSE — single combined AI call
  // Returns an EventSource-like interface. Caller provides onEvent callbacks.
  streamPatientSummary: (caseId, language = 'en', forceRegenerate = false, callbacks = {}) => {
    const token = getToken();
    const url = `${API_BASE}/ai/patient-summary-stream`;
    
    // Use fetch + ReadableStream for POST SSE (EventSource only supports GET)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000);

    // ─── TEST 1.14 · everything this attempt can observe about itself. ───
    // See the block above `aiAPI` for why this is a REPORT and not a repair.
    const t0 = Date.now();
    const m = {
      phase: 'request',      // request | http | body | read | parse | done
      status: null,
      contentType: null,
      chunks: 0,             // ★ reader chunks. 1 means BUFFERED, not streamed.
      bytes: 0,
      frames: 0,             // SSE records handed to a callback
      firstFrameMs: null,
    };
    _sseAttempts += 1;
    // ONE line, and `reason` is never empty: message -> name -> the phase it died in.
    // A failure that cannot say which of those it fell back to is the defect this fixes.
    const diagnose = (err) => {
      const reason = (err && err.message) || (err && err.name) ||
        `no reason available from the engine (died in phase "${m.phase}")`;
      if (m.frames > 0 && m.chunks <= 1) m.note = 'the whole body arrived in ONE reader chunk — '
        + 'this response was BUFFERED, not streamed';
      return {
        error: reason,
        reason,
        name: (err && err.name) || null,
        phase: m.phase,
        status: m.status,
        contentType: m.contentType,
        elapsedMs: Date.now() - t0,
        chunks: m.chunks,
        bytes: m.bytes,
        frames: m.frames,
        firstFrameMs: m.firstFrameMs,
        url,
        attempts: _sseAttempts,
        progressive: _sseProgressive,
        note: m.note || null,
      };
    };
    const shout = (d) => {
      console.error(
        `[SSE] stream failed in phase=${d.phase} reason="${d.reason}" name=${d.name} ` +
        `status=${d.status} contentType=${d.contentType} elapsedMs=${d.elapsedMs} ` +
        `chunks=${d.chunks} bytes=${d.bytes} frames=${d.frames} firstFrameMs=${d.firstFrameMs} ` +
        `url=${d.url}` + (d.note ? ` note=${d.note}` : ''));
      // ★ THE SENTENCE THE ROW ASKED FOR. A fallback that has fired on every attempt is
      //   not a fallback, it is the only path, and until now nothing could say so.
      if (d.progressive === 0 && d.attempts >= 1) {
        console.error(
          `[SSE] the streamed summary has delivered progressively on 0 of ${d.attempts} ` +
          'attempt(s) in this session. It is not degrading to the fallback, it IS the ' +
          'fallback. The patient waits at a blank screen for the full generation. ' +
          'See the TEST 1.14 block above aiAPI in src/services/api.js — the cause is the ' +
          'transport (Mangum buffers; API Gateway cuts at 30,000 ms and answers without ' +
          'CORS headers), NOT this client.');
      }
    };

    const run = async () => {
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ caseId, language, forceRegenerate }),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        m.phase = 'http';
        m.status = response.status;
        m.contentType = response.headers?.get?.('content-type') ?? null;
        
        if (!response.ok) {
          const errorText = await response.text();
          // PROD-1225: the one raw fetch that reaches a consent-gated path. Same signal as apiFetch.
          if (response.status === 403) {
            let body = null;
            try { body = JSON.parse(errorText); } catch (_) { body = null; }
            signalAiConsentRequired(response.status, body);
          }
          const d = diagnose(new Error(`HTTP ${response.status} (error body ${errorText.length} chars)`));
          shout(d);
          callbacks.onError?.(d);
          return;
        }

        // ★ `response.body` is null for an opaque or bodyless response, and
        //   `.getReader()` on null threw a TypeError that was reported as a STREAM
        //   error — the wrong phase, and the reason a real transport failure and a
        //   missing body read identically in the log.
        m.phase = 'body';
        if (!response.body) {
          const d = diagnose(new Error('the response carried no readable body'));
          shout(d);
          callbacks.onError?.(d);
          return;
        }

        m.phase = 'read';
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          m.chunks += 1;
          m.bytes += value?.byteLength || 0;
          
          buffer += decoder.decode(value, { stream: true });
          
          // Parse SSE events from buffer
          const events = buffer.split('\n\n');
          buffer = events.pop(); // Keep incomplete last chunk
          
          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            
            const lines = eventBlock.split('\n');
            let eventType = '';
            let eventData = '';
            
            for (const line of lines) {
              if (line.startsWith('event: ')) eventType = line.slice(7).trim();
              else if (line.startsWith('data: ')) eventData = line.slice(6);
            }
            
            if (!eventType || !eventData) continue;
            m.frames += 1;
            if (m.firstFrameMs === null) m.firstFrameMs = Date.now() - t0;
            
            try {
              const parsed = JSON.parse(eventData);
              
              switch (eventType) {
                case 'narrative':
                  callbacks.onNarrative?.(parsed.content);
                  break;
                case 'dx':
                  callbacks.onDx?.(parsed);
                  break;
                case 'exams':
                  callbacks.onExams?.(parsed);
                  break;
                case 'questions':
                  callbacks.onQuestions?.(parsed);
                  break;
                case 'redflags':
                  callbacks.onRedFlags?.(parsed);
                  break;
                case 'clinical':
                  callbacks.onClinical?.(parsed);
                  break;
                case 'complete':
                  callbacks.onComplete?.(parsed);
                  break;
                case 'error':
                  // A server-SENT error. Kept distinct from a transport failure —
                  // it is the one shape where the API really did tell us why.
                  m.phase = 'server-event';
                  callbacks.onError?.({ ...diagnose(new Error('server sent event: error')), server: parsed });
                  break;
              }
            } catch (parseErr) {
              m.parseErrors = (m.parseErrors || 0) + 1;
              console.warn(`[SSE] frame ${m.frames} did not parse as JSON (${parseErr.name}); `
                + `${eventData.length} byte(s), event="${eventType}"`);
            }
          }
        }
        // ─── the body ended. SAY WHETHER IT STREAMED, even on success. ───
        // ★ A success that arrived in one chunk is the defect TEST 1.14 is about: the
        //   feature "works" and has silently stopped doing the thing it exists for.
        //   Nothing failed, so nothing used to be printed at all.
        m.phase = 'done';
        if (m.chunks > 1) {
          _sseProgressive += 1;
        } else if (m.frames > 0) {
          console.warn(
            `[SSE] the response completed but was NOT streamed: ${m.frames} frame(s) and ` +
            `${m.bytes} byte(s) arrived in ${m.chunks} reader chunk(s) after ` +
            `${Date.now() - t0} ms. The patient waited at a blank screen for a path built ` +
            'to render incrementally. Progressive delivery: ' +
            `${_sseProgressive} of ${_sseAttempts} attempt(s) this session. ` +
            'See the TEST 1.14 block above aiAPI.');
        } else {
          const d = diagnose(new Error('the body ended with zero SSE frames'));
          shout(d);
          callbacks.onError?.(d);
        }
      } catch (err) {
        clearTimeout(timeoutId);
        if (err.name === 'AbortError') {
          // ★ Was reported as 'Stream timed out' — which was a GUESS. An abort is also
          //   what the caller's own `streamRef.current.abort()` does on unmount and on
          //   Regenerate, so this line blamed a timeout for a deliberate cancellation.
          const d = diagnose(err);
          d.error = d.reason = `the request was aborted after ${d.elapsedMs} ms in phase "${d.phase}" `
            + '(the 120 s guard, an unmount, or a Regenerate — this client cannot tell them apart)';
          console.warn('[SSE] ' + d.reason);
          callbacks.onError?.(d);
        } else {
          const d = diagnose(err);
          shout(d);
          callbacks.onError?.(d);
        }
      }
    };
    
    run();
    
    // Return abort function
    return { abort: () => { clearTimeout(timeoutId); controller.abort(); } };
  },
  
  // Extract data from ID or Insurance card images
  // PROD-1190: opts.patientState (the patient's address state, when known) rides with an
  //   insurance card so a regional insurer's plans are narrowed to it (utils/payerPick.js).
  extractCardData: async (imageBase64, cardType = 'ID', side = 'front', opts = {}) => {
    try {
      const response = await apiFetch('/ai/extract-card-data', {
        method: 'POST',
        body: JSON.stringify(extractCardBody(imageBase64, cardType, side, opts)),
      });
      return response;
    } catch (err) {
      console.error('[AI] Card extraction error:', err);
      return { success: false, extracted: {} };
    }
  },
  
  // Check AI service status
  status: async () => {
    try {
      const response = await apiFetch('/ai/status');
      return response;
    } catch {
      return { available: false };
    }
  },

  /**
   * The STORED agent conversation for one case. v242.156.0 · CASE-CHAT-PERSIST-1.
   *
   * Dr. Rami, D348: the case chat is the window with the important case information in
   * it, and it was stored nowhere. It is stored now, on `cases.case_chats`, alongside
   * the physician's Ask Orella thread in the same column.
   *
   * ★★ `surface=agent` IS SENT AND IT IS NOT COSMETIC. The server decides which
   * surfaces a caller may read and refuses the rest with a 403 rather than an empty
   * list — a patient may read the agent conversation and NOT the physician's Ask Orella
   * thread, which carries uncited model reasoning written about them rather than to
   * them. Naming our surface is how this stays right rather than relying on the default.
   */
  caseChatHistory: async (caseId) => apiFetch(`/cases/${caseId}/chat?surface=agent`),

  // Orella Agent chat
  agentChat: async (message, conversationHistory = [], caseContext = {}, caseId = '') => {
    try {
      const response = await apiFetch('/agent/chat', {
        method: 'POST',
        body: JSON.stringify({ message, conversationHistory, caseContext, caseId }),
      });
      return response;
    } catch (err) {
      console.error('[Agent] Chat error:', err);
      const _lang = (() => { try { return localStorage.getItem('orella-language') || 'en' } catch { return 'en' } })()
      return {
        message: _lang === 'es'
          ? "Estoy teniendo un pequeño problema ahora. Intente de nuevo, o si su pregunta es urgente, siempre puede comunicarse directamente con el consultorio de su doctor."
          : "I'm having a little trouble right now. Give it another try, or if your question is urgent, you can always reach out to your doctor's office directly.",
        suggestions: [],
        error: true,
        errorStatus: err?.status ?? null,
      };
    }
  },

  // Symptom triage — routes patient description to matching specialties
  agentTriage: async (message, language = 'en') => {
    try {
      const response = await apiFetch('/agent/triage', {
        method: 'POST',
        body: JSON.stringify({ message, language }),
      });
      // R21 / PROD-164 (orella-api D5b, Alex Q46): a tripped message comes back with
      // `emergencyTrip` = { language, route: 'triage', at } (no text). No case exists yet, so it
      // waits for the next case creation (utils/emergencyTrips.js).
      if (response && response.emergencyTrip) rememberEmergencyTrip(response.emergencyTrip);
      return response;
    } catch (err) {
      console.error('[Agent] Triage error:', err);
      return { matches: [], error: true };
    }
  },

  // v165.0.0: Generate smart preparation instructions for ordered tests
  // Uses Haiku for speed. Returns steps, warnings, note tailored to patient profile.
  generateOrderPrep: async (caseId, testType, tests) => {
    try {
      const response = await apiFetch('/ai/order-prep', {
        method: 'POST',
        body: JSON.stringify({ caseId, testType, tests }),
        timeout: 15000, // 15s — Haiku is fast
      });
      return response;
    } catch (err) {
      console.warn('[AI] Order prep generation failed:', err);
      return null;
    }
  },

  // v237.0.0: Generate plain-language "Learn more" education for care orders.
  // Uses Haiku. Returns { education: { education: [{what,why,how}], ... } }.
  generateOrderEducation: async (caseId, orderType, items) => {
    try {
      const response = await apiFetch('/ai/order-education', {
        method: 'POST',
        body: JSON.stringify({ caseId, orderType, items }),
        timeout: 18000, // 18s — multiple items, still Haiku
      });
      return response;
    } catch (err) {
      console.warn('[AI] Order education generation failed:', err);
      return null;
    }
  },

  // v238.0.0: Generate consent-level counseling primer for procedures.
  // Uses Sonnet (quality-critical risk language). Cached on the order.
  // Returns { counseling: { counseling: [{type,summary,what,why,expect,benefits,risks[],alternatives,conditional}], ... } }.
  generateProcedureCounseling: async (caseId, items) => {
    try {
      const response = await apiFetch('/ai/procedure-counseling', {
        method: 'POST',
        body: JSON.stringify({ caseId, items }),
        timeout: 35000, // 35s — Sonnet, multi-section per procedure
      });
      return response;
    } catch (err) {
      console.warn('[AI] Procedure counseling generation failed:', err);
      return null;
    }
  },

  // v237.2.0: Plain-language education for ordered labs/imaging.
  // Uses Haiku. Returns { education: { education: [{reason,whatItIs,whatHappens}], ... } }.
  generateTestEducation: async (caseId, testType, items) => {
    try {
      const response = await apiFetch('/ai/test-education', {
        method: 'POST',
        body: JSON.stringify({ caseId, testType, items }),
        timeout: 18000, // 18s — Haiku
      });
      return response;
    } catch (err) {
      console.warn('[AI] Test education generation failed:', err);
      return null;
    }
  },

  // v237.4.0: Plain-language education for ordered DME (equipment & supplies).
  // Uses Haiku. Returns { education: { education: [{whatItIs,howToUse,caring}], ... } }.
  generateDmeEducation: async (caseId, items) => {
    try {
      const response = await apiFetch('/ai/dme-education', {
        method: 'POST',
        body: JSON.stringify({ caseId, items }),
        timeout: 18000, // 18s — Haiku
      });
      return response;
    } catch (err) {
      console.warn('[AI] DME education generation failed:', err);
      return null;
    }
  },
};

// Transcription API (AWS Transcribe Medical)
export const transcriptionAPI = {
  // The status probe was removed with the route it called (WHISPER-REMOVAL-1).
  // It reported whether the Whisper engine was configured; it never was, and
  // nothing in this app read the answer.
  
  

  // v242.52.0 (Session D · AI-2/AI-4) — mint a presigned Transcribe streaming
  // WebSocket URL. Audio streams client -> AWS directly and never transits the
  // API. The URL expires 300s after minting, so call this on mic press, never
  // on page load: a stale URL then fails at the handshake (recoverable) rather
  // than mid-stream (not).
  streamUrl: async ({ surface = 'patient', language = 'en', specialty, showSpeakerLabels } = {}) => {
    const response = await apiFetch('/transcription/stream-url', {
      method: 'POST',
      body: JSON.stringify({ surface, language, specialty, showSpeakerLabels }),
    });
    return response;
  },

  // v242.57.0 (Session D · D-TIDY) — subtractive cleanup of a streamed
  // transcript via Bedrock (ai_service.client, inside the AWS BAA). Strips
  // filler and false starts, adds punctuation, and NEVER resolves ambiguity:
  // a garbled passage stays garbled so the patient sees it and fixes it.
  //
  // Returns { tidied, wasTidied } on success; { wasTidied: false, reason }
  // when the pass declined or a guard tripped. This is COSMETIC — every
  // failure mode must fall back to the raw transcript silently.
  //
  // 12s, not the 30s default: this sits under a spinner the patient is
  // watching, and a slow cosmetic pass is worth abandoning. The deadline
  // goes through `timeout` (a caller `signal` only aborts, it sets no deadline).
  tidy: async (transcript, { timeout = 12000 } = {}) => {
    const response = await apiFetch('/transcription/tidy', {
      method: 'POST',
      body: JSON.stringify({ transcript }),
      timeout,
    });
    return response;
  },
};

// Favorites API (v150.4.0)
export const favoritesAPI = {
  getDoctors: async () => {
    try {
      const result = await apiFetch('/favorites/doctors');
      return result?.favorite_doctors || [];
    } catch (error) {
      console.log('Favorites fetch error:', error);
      return [];
    }
  },
  addDoctor: async (doctorId) => {
    return apiFetch(`/favorites/doctors/${doctorId}`, { method: 'POST' });
  },
  removeDoctor: async (doctorId) => {
    return apiFetch(`/favorites/doctors/${doctorId}`, { method: 'DELETE' });
  },
};

// Doctors API - for finding available doctors
// ★ s98 Phase 6 (D464, D466) — the US doctor directory (CMS NPPES, bulk-loaded): patients
//   only, 30 searches a minute. params: { q?, zip?, taxonomy? ('207Y,363L'), radiusMiles?, limit? }.
// s106 (canvas "Orella Referrals Screens" A2-A8): the patient's referral request to their primary care
//   doctor (API D503/D507/D515). A PCP on Orella gets it in the inbox; one outside Orella with a fax is
//   faxed at once (consent required); a failed fax is resent with `resendFax`.
export const referralRequestsAPI = {
  listForCase: async (caseId) => apiFetch(`/cases/${caseId}/referral-requests`),
  requirement: async (caseId) => apiFetch(`/cases/${caseId}/referral-requirement`),
  create: async (caseId, { pcp, includeCaseReport = true, consent = false }) => apiFetch(`/cases/${caseId}/referral-requests`, {
    method: 'POST', body: JSON.stringify({ pcp, includeCaseReport, consent }),
  }),
  resendFax: async (requestId, { faxNumber, consent = false } = {}) => apiFetch(`/referral-requests/${requestId}/fax`, {
    method: 'POST', body: JSON.stringify(faxNumber ? { consent, faxNumber } : { consent }),
  }),
  // multipart, so not through apiFetch (it always sends JSON): the same shape as filesAPI.upload.
  uploadDocument: async (requestId, file) => {
    const token = getToken()
    const fd = new FormData()
    fd.append('file', file, file.name || 'referral')
    const response = await fetch(`${API_BASE}/referral-requests/${requestId}/document`, {
      method: 'POST', headers: token ? { 'Authorization': `Bearer ${token}` } : {}, body: fd,
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok) {
      const e = new Error(body?.error || 'Upload failed. Please try again.')
      e.status = response.status
      e.code = body?.code
      throw e
    }
    return body
  },
  answer: async (requestId, { documentKey, fields }) => apiFetch(`/referral-requests/${requestId}/answer`, {
    method: 'POST', body: JSON.stringify(documentKey ? { documentKey, fields } : { fields }),
  }),
  cancel: async (requestId) => apiFetch(`/referral-requests/${requestId}/cancel`, { method: 'POST', body: JSON.stringify({}) }),
}

export const providersAPI = {
  /** PROD-1115: `{ signal }` aborts the request (utils/directorySearch: an older search, a closed list). */
  search: async (params = {}, { signal } = {}) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
    }
    return apiFetch(`/providers/search?${qs.toString()}`, signal ? { signal } : {})
  },
  /** s101 Phase 4 (x_24): the 5-digit ZIP centroids of up to twelve 3-digit areas; the phone picks
   *  its own nearest ZIP from them (utils/zip5Locate.js). The GPS point is never sent. */
  zipAreas: async (prefixes) =>
    apiFetch(`/providers/zip-areas?zip3=${encodeURIComponent((prefixes || []).slice(0, 12).join(','))}`),
  /** s110 (b77, mockup P2): the primary care doctor finder — GET /providers/search?mode=pcp. `npi` alone finds
   *  exactly that clinician; otherwise `q` (a doctor's or a practice's name) within `place` (a ZIP, a city and/or
   *  a state). `zip` is the patient's ZIP, only to order by distance. 503 code pcp_search_unavailable until API
   *  migration v243_360_0 is applied (the caller then falls back to search()). */
  pcpSearch: async ({ q = '', npi = '', place = '', zip = '', taxonomy = '' } = {}) => {
    const qs = new URLSearchParams({ mode: 'pcp' })
    for (const [k, v] of Object.entries({ q, npi, place, zip, taxonomy })) {
      if (v !== undefined && v !== null && String(v).trim() !== '') qs.set(k, String(v).trim())
    }
    return apiFetch(`/providers/search?${qs.toString()}`)
  },
  /** s110: one directory doctor's offices, nearest the ZIP first (the office picker). */
  offices: async (npi, zip) => {
    const qs = new URLSearchParams()
    if (zip) qs.set('zip', String(zip))
    return apiFetch(`/providers/${encodeURIComponent(String(npi || ''))}/offices?${qs.toString()}`)
  },
}

export const doctorsAPI = {
  // List doctors available for patient cases
  list: async (filters = {}) => {
    try {
      let endpoint = '/doctors/available';
      const params = new URLSearchParams();
      
      if (filters.specialty) params.append('specialty', filters.specialty);
      
      if (params.toString()) endpoint += `?${params.toString()}`;
      
      const result = await apiFetch(endpoint);
      return result || [];
    } catch (error) {
      console.log('Doctors API error:', error);
      // Return empty array - no mock data, show "no doctors" message
      return [];
    }
  },
  
  // Get doctor by ID
  get: async (doctorId) => {
    try {
      return await apiFetch(`/doctors/${doctorId}`);
    } catch (error) {
      console.log('Doctor fetch error:', error);
      return null;
    }
  },
  
  // Get full doctor profile (bundled: profile + insurance + appointment types)
  getFullProfile: async (doctorId) => {
    try {
      return await apiFetch(`/doctors/${doctorId}/full-profile`);
    } catch (error) {
      console.log('Doctor full profile fetch error:', error);
      return null;
    }
  },
  
  // Get doctor's availability schedule
  getAvailability: async (doctorId) => {
    try {
      return await apiFetch(`/doctors/${doctorId}/availability`);
    } catch (error) {
      console.log('Availability fetch error:', error);
      return { enabled: false, schedule: null };
    }
  },
  
  // Get available appointment slots for a doctor
  // v127.0: added category param for duration-aware slot generation
  getAvailableSlots: async (doctorId, days = 14, category = 'initial') => {
    try {
      return await apiFetch(`/doctors/${doctorId}/slots?days=${days}&category=${category}`);
    } catch (error) {
      console.log('Slots fetch error:', error);
      // TEST 1.35 — `fetchFailed` keeps "I could not read the calendar" apart from
      // "scheduling is off" (both are enabled:false). utils/bookableSlots.js reads it.
      return { enabled: false, slots: [], fetchFailed: true };
    }
  },
};

// Notifications API
export const notificationsAPI = {
  list: async (unreadOnly = false) => {
    try {
      const params = new URLSearchParams({ user_type: 'patient' });
      if (unreadOnly) params.append('unread_only', 'true');
      return await apiFetch(`/notifications?${params}`);
    } catch (error) {
      console.log('Notifications fetch error:', error);
      return { notifications: [], unreadCount: 0 };
    }
  },
  
  markRead: async (notifId) => {
    return apiFetch(`/notifications/${notifId}/read`, { method: 'PUT' });
  },
  
  markAllRead: async () => {
    return apiFetch('/notifications/read-all?user_type=patient', { method: 'PUT' });
  },

  // v220.12.3 — destructive: permanently removes notification(s)
  delete: async (notifId) => {
    return apiFetch(`/notifications/${notifId}`, { method: 'DELETE' });
  },

  clearAll: async ({ onlyRead = false } = {}) => {
    const params = new URLSearchParams({ user_type: 'patient' });
    if (onlyRead) params.append('only_read', 'true');
    return apiFetch(`/notifications/clear?${params}`, { method: 'DELETE' });
  },

  // v179.0.0: Notification preferences
  getPreferences: async () => {
    try {
      return await apiFetch('/notification-preferences');
    } catch (error) {
      console.log('Notification preferences fetch error:', error);
      return { masterToggles: { push: true, email: true, sms: false }, categoryPreferences: {}, categoryMap: {} };
    }
  },

  updatePreferences: async (prefs) => {
    return apiFetch('/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    });
  },
};

// v238.22.0: Data export — uses apiFetch so the token comes from the same
// sessionStorage source as every other call (the old inline fetch read
// localStorage, which is always empty → 401).
export const dataExportAPI = {
  request: async (type = 'export_only') => {
    return apiFetch('/data-export/request', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  },
  status: async () => {
    return apiFetch('/data-export/status', { method: 'GET' });
  },
  // P5 D4 (PROD-171): the patient builds her own export and downloads it through a 600 s
  // link. Answers {request_id, complete, files, failed, download_url, expires_in}; the
  // route reads, zips and uploads inside the gateway's window, so the client waits a
  // little longer than the gateway does and a gateway answer arrives first.
  generate: async () => {
    return apiFetch('/data-export/generate', {
      method: 'POST',
      body: JSON.stringify({}),
      timeout: 35000,
    });
  },
  // A fresh 600 s link to an export already built: {request_id, download_url, expires_in}.
  download: async (requestId) => {
    return apiFetch(`/data-export/${encodeURIComponent(requestId)}/download`, { method: 'GET' });
  },
};

// Files API — Supabase Storage helpers
// v220.1 — Case Messenger API
// Patient ⇄ MD threaded messaging on a finalized case.
// Storage: cases.case_messages (case-scoped, continuous across encounters).
export const messengerAPI = {
  /**
   * Send an agent-prepped escalation to the MD.
   * @param {string} caseId
   * @param {object} prep  { body, distilled_question, verbatim_excerpt, prep_context, category }
   * @returns {Promise<{thread_id, message_id, encounter_number}>}
   */
  send: async (caseId, prep) => {
    return apiFetch(`/cases/${caseId}/messages`, {
      method: 'POST',
      body: JSON.stringify(prep),
    });
  },

  /**
   * Reply within an existing thread.
   * @returns {Promise<{message_id, encounter_number}>}
   */
  reply: async (caseId, threadId, body) => {
    return apiFetch(`/cases/${caseId}/messages/${threadId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    });
  },

  /**
   * Fetch the full thread for a case. Marks all messages as read by the
   * requesting party as a side effect.
   * @returns {Promise<{caseId, messages, activeEncounter}>}
   */
  getThread: async (caseId) => {
    try {
      return await apiFetch(`/cases/${caseId}/messages`);
    } catch (err) {
      // Soft-fail — chat still works without thread merge
      console.log('Messenger getThread error:', err);
      return { caseId, messages: [], activeEncounter: 1 };
    }
  },
};

// Files API — Supabase Storage helpers

export const filesAPI = {
  /**
   * Upload a file to Supabase Storage via API.
   * @param {File|Blob} file - The file to upload
   * @param {string} storagePath - Path in storage bucket (e.g. 'cases/{id}/photos/img.jpg')
   * @returns {{ path, url, size }}
   */
  upload: async (file, storagePath) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file, file.name || 'file');
    formData.append('path', storagePath);
    
    const response = await fetch(`${API_BASE}/files/upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    // ★ P2 Release 3: a refusal keeps its status and code (400 storage_path_invalid,
    //   403 storage_path_denied). `.message` is still the server's `error` text, so every
    //   caller that showed err.message shows the same words as before.
    if (!response.ok) throw await uploadRefusalFromResponse(response);
    
    return response.json();
  },

  /**
   * v228.11.6: upload a base64 data URL (e.g. a captured card image) to storage,
   * returning { path, url } — so card images are stored by reference instead of
   * riding inside the profile JSON (which 413s on Vercel). Never throws.
   *
   * ★ P2 Release 3: a failed upload returns { path: null, url: null, error } with
   *   error = { status, code } (uploadFailureOf), no longer a bare null. The card
   *   screens read it and say the photo was not saved; before, a refusal filled the
   *   slot from the local preview and the save then dropped the image with no word.
   *   A non-data: input still returns null — there was nothing to upload.
   */
  uploadDataUrl: async (dataUrl, storagePathBase) => {
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const ext = (blob.type || '').includes('png') ? 'png' : (blob.type || '').includes('webp') ? 'webp' : 'jpg';
      const path = `${storagePathBase}.${ext}`;
      const file = new File([blob], path.split('/').pop(), { type: blob.type || 'image/jpeg' });
      const res = await filesAPI.upload(file, path);
      return res && res.path ? { path: res.path, url: res.url } : { path: null, url: null, error: { status: 0, code: null } };
    } catch (e) {
      const failure = uploadFailureOf(e);
      console.warn('[files] data-url upload failed:', failure.status, failure.code || '');
      return { path: null, url: null, error: uploadFailureOf(e) };
    }
  },

  /**
   * Get a signed URL for a storage path.
   */
  getSignedUrl: async (storagePath, expiresIn = 3600) => {
    const result = await apiFetch('/files/signed-url', {
      method: 'POST',
      body: JSON.stringify({ path: storagePath, expiresIn }),
    });
    return result.signedUrl;
  },

  /**
   * Get signed URLs for multiple storage paths at once.
   */
  /**
   * s101 Phase 3 (x_19): a fresh link to a case's stored document, with the case named so the
   * API can admit the patient on that case (require_storage_key_access case_hint). The link the
   * case carried was signed on read and is dead an hour later.
   */
  signedUrlForCase: async (storagePath, caseId, expiresIn = 3600) => {
    const result = await apiFetch('/files/signed-url', {
      method: 'POST',
      body: JSON.stringify({ path: storagePath, caseId, expiresIn }),
    });
    return result.signedUrl;
  },

  getSignedUrls: async (paths, expiresIn = 3600) => {
    const result = await apiFetch('/files/signed-urls', {
      method: 'POST',
      body: JSON.stringify({ paths, expiresIn }),
    });
    return result.urls || {};
  },

  /**
   * P2 Release 3 (PROD-167): the batch with its per-path verdict. orella-api P2-8 maps a
   * path the caller may not read to null in `urls` AND lists it in `denied`; the batch
   * never fails as a whole. getSignedUrls above keeps only `urls`, so a refused file and a
   * missing one looked the same. The records export needs to tell them apart.
   * @returns {{ urls: object, denied: string[] }}
   */
  getSignedUrlsWithDenied: async (paths, expiresIn = 3600) => {
    const result = await apiFetch('/files/signed-urls', {
      method: 'POST',
      body: JSON.stringify({ paths, expiresIn }),
    });
    return { urls: result.urls || {}, denied: Array.isArray(result.denied) ? result.denied : [] };
  },
};

/**
 * Resolve a file reference to a viewable URL.
 * - base64 data URLs → returned as-is
 * - http URLs → returned as-is
 * - storage paths → fetch signed URL from API
 */
export const resolveFileUrl = async (fileRef) => {
  if (!fileRef) return null;
  if (typeof fileRef !== 'string') return null;
  if (fileRef.startsWith('data:') || fileRef.startsWith('http')) return fileRef;
  // It's a storage path — get signed URL
  try {
    return await filesAPI.getSignedUrl(fileRef);
  } catch {
    console.warn('[resolveFileUrl] Failed to get a signed URL');
    return fileRef;
  }
};

// ==========================================
// ENCOUNTERS API (v120)
// ==========================================
export const encountersAPI = {
  // Get all encounters for a case
  list: async (caseId) => {
    return apiFetch(`/cases/${caseId}/encounters`)
  },

  // Get specific encounter
  get: async (caseId, encounterNumber) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}`)
  },

  // Create new encounter
  create: async (caseId, data = {}) => {
    return apiFetch(`/cases/${caseId}/encounters`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Submit pre-visit report (patient → doctor)
  submitPreVisitReport: async (caseId, encounterNumber, reportData) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}/pre-visit-report`, {
      method: 'POST',
      body: JSON.stringify(reportData),
    })
  },

  // Acknowledge encounter diagnosis (patient reviewed)
  acknowledge: async (caseId, encounterNumber) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}/acknowledge`, {
      method: 'PUT',
      body: JSON.stringify({}),
    })
  },

  // v121.9.0: Report a positive finding during observation → triggers follow-up path
  reportFinding: async (caseId, encounterNumber, { finding = '', notes = '', requestedDate = '', requestedTime = '' } = {}) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}/appointment-request`, {
      method: 'POST',
      body: JSON.stringify({ finding, notes, requestedDate, requestedTime }),
    })
  },

  // v125.2.1: Cancel a pending observation bridge appointment request — reverts to observation
  cancelAppointmentRequest: async (caseId, encounterNumber) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}/cancel-appointment-request`, {
      method: 'DELETE',
    })
  },

  // ★★★ D332(f)(j) — SUBMIT AN OBSERVATION CHECK-UP. Three defects lived in the twelve
  //   lines this replaces, and each of them returned quietly.
  //   (1) METHOD. It sent PUT. The path is registered POST-only, so every check-up 405'd
  //       for the whole life of the feature; `pre_visit_report.checkIns` had five readers
  //       and ZERO writers because of this line.
  //   (2) THE PAYLOAD. It destructured `{ notes, reportedChange }` and dropped everything
  //       else — including `aiAnswers`, which the AI check-up caller has been passing all
  //       along. The answers were built, sent, and thrown away here.
  //   (3) THE NAME. `check_in` already means appointment ARRIVAL everywhere in this system
  //       (CheckInModal, verify_check_in, appointment_check_in, check_in.billed). This is a
  //       clinical check-up. The server still accepts the old `type` spelling, because a
  //       build in the field sends it and a rename is not a reason to drop a patient's
  //       answer — but nothing new should write it.
  submitCheckUp: async (caseId, encounterNumber,
                        { notes = '', reportedChange = false, aiAnswers = null, ...rest } = {}) => {
    return apiFetch(`/cases/${caseId}/encounters/${encounterNumber}/pre-visit-report`, {
      method: 'POST',
      body: JSON.stringify({
        ...rest,
        type: 'observation_checkup',
        notes,
        reportedChange,
        ...(aiAnswers ? { aiAnswers } : {}),
        date: new Date().toISOString(),
      }),
    })
  },
}

// ==================== MEDICATION LEDGER ====================
export const medicationsAPI = {
  list: async (includePast = false) => {
    const params = includePast ? '?include_past=true' : ''
    const res = await fetchWithAuth(`${API_BASE}/medications${params}`)
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to fetch medications')
    return data.medications || []
  },

  add: async (medication) => {
    const res = await fetchWithAuth(`${API_BASE}/medications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(medication),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to add medication')
    return data.medication
  },

  update: async (medId, updates) => {
    const res = await fetchWithAuth(`${API_BASE}/medications/${medId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to update medication')
    return data.medication
  },

  /**
   * "Still taking it" — reopen a course that ended, or restart one the patient stopped.
   * ★ The SAME row comes back active (his ruling 2026-09-08), so its provenance, its case
   *   and its history survive; the server stashes what was prescribed into
   *   `original_end_date` before clearing `end_date`.
   * ★★ A 403 here is not a failure to handle quietly: it is the server saying a DOCTOR
   *   stopped this one, and the patient needs to read that sentence.
   */
  resume: async (medId) => {
    const res = await fetchWithAuth(`${API_BASE}/medications/${medId}/resume`, { method: 'PUT' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw Object.assign(new Error(data.error || 'Failed to resume medication'),
                                     { status: res.status, error: data.error });
    return data;
  },

  discontinue: async (medId, reason) => {
    const res = await fetchWithAuth(`${API_BASE}/medications/${medId}/discontinue`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to discontinue medication')
    return data.medication
  },

  remove: async (medId) => {
    const res = await fetchWithAuth(`${API_BASE}/medications/${medId}`, { method: 'DELETE' })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to delete medication')
    return data
  },
}

// ── Insurance Registry (v152.0.0) ──
// Public endpoints — no auth needed. Fetches from admin-managed DB tables
// with bundled medicalLists.js as fallback if API unreachable.
export const insuranceRegistryAPI = {
  getCarriers: async () => {
    try {
      const res = await fetch(`${API_BASE}/insurance/carriers`)
      const data = await res.json()
      return data.carriers || []
    } catch (err) {
      console.warn('[InsuranceRegistry] Carriers fetch failed, using bundled fallback:', err)
      return null // caller falls back to medicalLists.js
    }
  },

  getIPAs: async (region) => {
    try {
      const url = region ? `${API_BASE}/insurance/ipas?region=${encodeURIComponent(region)}` : `${API_BASE}/insurance/ipas`
      const res = await fetch(url)
      const data = await res.json()
      return data.ipas || []
    } catch (err) {
      console.warn('[InsuranceRegistry] IPAs fetch failed:', err)
      return null
    }
  },

  // v224.5.7: live Stedi payer typeahead (authenticated). Returns canonical
  // payers so the selected carrier carries a real Stedi payer ID.
  // v242.64.0 (TF-11): the payer directory is a REMOTE service, so "your insurer
  // is not in the list" and "we could not ask" are different answers and the UI
  // must not merge them. /payers/search answers HTTP 200 with
  // { error, payers: [] } when Stedi is unreachable — and every caller dropped
  // `error` on the floor, so a dead credential rendered as "No matches."
  searchPayersDetailed: async (q) => {
    try {
      const data = await apiFetch(`/payers/search?q=${encodeURIComponent(q)}`)
      return { payers: (data && data.payers) || [], error: (data && data.error) || null }
    } catch (err) {
      console.warn('[InsuranceRegistry] Payer search failed:', err)
      return { payers: [], error: (err && err.message) || 'Payer search unavailable' }
    }
  },
  searchPayers: async (q) => {
    try {
      const data = await apiFetch(`/payers/search?q=${encodeURIComponent(q)}`)
      return (data && data.payers) || []
    } catch (err) {
      console.warn('[InsuranceRegistry] Payer search failed:', err)
      return []
    }
  },
}

// ── Patient Visit Agreement sign-link (v225.2.0, Phase B) ──
// PUBLIC — no auth. Token-scoped; the patient is not logged in.
export const signAgreementAPI = {
  get: async (token) => apiFetch(`/sign-agreement/${token}`),
  // s101 (D496): agreedPractice + practiceTemplateId when the physician's own agreement was shown.
  submit: async (token, { signature, agreed, agreedPractice, practiceTemplateId }) =>
    apiFetch(`/sign-agreement/${token}`, {
      method: 'POST',
      body: JSON.stringify(practiceTemplateId ? { signature, agreed, agreedPractice: !!agreedPractice, practiceTemplateId } : { signature, agreed }),
    }),
}

// ── Active agreement template (v225.4.0, Phase B-3) ──
// Authed. Resolves the active admin-published agreement for the case's scope
// (global default fallback) so the in-app flow reads + binds the SAME version
// as the sign-link flow.
export const agreementTemplateAPI = {
  active: async ({ caseId, doctorId } = {}) => {
    const qs = caseId ? `?case_id=${caseId}` : (doctorId ? `?doctor_id=${doctorId}` : '')
    const res = await apiFetch(`/agreement-template/active${qs}`)
    return res?.template || null
  },
}

export const pushAPI = {
  // Public key the browser needs to build a push subscription (no auth required).
  getVapidKey: async () => apiFetch('/push/vapid-public-key'),
  // Register this device's subscription with the API.
  subscribe: async (subscription, userAgent) =>
    apiFetch('/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({ subscription, userAgent }),
    }),
  // Remove this device's subscription by endpoint.
  unsubscribe: async (endpoint) =>
    apiFetch('/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
  // s119 (D532): this iPhone's APNs device token (services/nativePush.js).
  registerApnsToken: async (token) =>
    apiFetch('/push/apns-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
  removeApnsToken: async (token) =>
    apiFetch('/push/apns-token/remove', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),
};

// ★★★ REP-10 / REP-11 — "Report a problem" (v242.251.0). The ONLY client of the two patient
//   support routes (orella-api D7: support_submit_ticket_patient and
//   support_patient_ticket_attachment). NOT LIVE: every caller sits behind
//   REPORT_PROBLEM_ENABLED (src/config/featureFlags.js), and this object is deliberately not in
//   the default export below, so with the flag off nothing reaches it.
// ★★ '/support/tickets/patient', NEVER '/support/tickets': the public route takes a typed name
//   and e-mail; this one takes identity from the token only. check-report-problem.mjs arm A.
// ★★ Files go one per call, multipart field `file`, AFTER the ticket answered 201 with its
//   ticket_id (utils/bugReport.js fileReport). Never filesAPI.upload: its storage path is
//   client-chosen; here the server chooses the path. A refusal keeps its status and code
//   (409 attachment_window_closed, 413 attachment_limit, 415 attachment_type) — apiFetch would
//   rewrite a 413 into a generic message, so the upload does not go through it.
export const supportAPI = {
  submitPatientTicket: async (payload) =>
    apiFetch('/support/tickets/patient', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  uploadPatientAttachment: async (ticketId, file, uploadName) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file, uploadName);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    let response;
    try {
      response = await fetch(
        `${API_BASE}/support/tickets/patient/${encodeURIComponent(ticketId)}/attachments`,
        {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData,
          signal: controller.signal,
        },
      );
    } catch (err) {
      clearTimeout(timeoutId);
      throw { status: err && err.name === 'AbortError' ? 408 : 0, code: null };
    }
    clearTimeout(timeoutId);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw { status: response.status, code: (data && data.code) || null, error: (data && data.error) || null };
    }
    return data;
  },
};

// v238.3.0 — Custom Document Handler (patient side). Patient uploads a blank
// form, submits it to their doctor to fill/sign, and views/downloads the
// finished signed document when it comes back.
export const customDocAPI = {
  /** Upload a blank form (PDF/PNG/JPG). Returns upload metadata incl. AcroForm detection. */
  patientUpload: async (caseId, file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('file', file, file.name || 'form');
    const response = await fetch(`${API_BASE}/cases/${caseId}/custom-doc/patient-upload`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Upload failed');
    return data;
  },

  /** List this case's documents (we filter to docType === 'custom' client-side). */
  listRequests: async (caseId) => apiFetch(`/cases/${caseId}/case-requests`),

  /** Submit a custom document request to the doctor (status forced to 'requested' server-side). */
  createRequest: async (caseId, { config, patientNote, title } = {}) =>
    apiFetch(`/cases/${caseId}/case-requests`, {
      method: 'POST',
      body: JSON.stringify({ docType: 'custom', title, config: config || {}, patientNote: patientNote || '' }),
    }),

  /** Refresh a 1h signed URL for a stored doc (e.g. the finished PDF to download). */
  signUrl: async (caseId, storagePath) =>
    apiFetch(`/cases/${caseId}/custom-doc/sign-url`, {
      method: 'POST',
      body: JSON.stringify({ storagePath }),
    }),
};

export default {
  auth: authAPI,
  cases: casesAPI,
  questionnaires: questionnairesAPI,
  profile: profileAPI,
  medications: medicationsAPI,
  ai: aiAPI,
  transcription: transcriptionAPI,
  doctors: doctorsAPI,
  notifications: notificationsAPI,
  push: pushAPI,
  customDoc: customDocAPI,
  files: filesAPI,
  encounters: encountersAPI,
  insuranceRegistry: insuranceRegistryAPI,
  signAgreement: signAgreementAPI,
  agreementTemplate: agreementTemplateAPI,
  resolveFileUrl,
};

// s101 (D496): the physician's OWN patient agreement, signed WITH Orella's at the share step.
//   forDoctor -> { agreement: { templateId, version, title, sections (a PDF entry carries a fresh url),
//   provider, practice } | null }. casePdf -> a fresh link to the PDF this case's patient signed.
export const practiceAgreementAPI = {
  forDoctor: async (doctorId) => {
    if (!doctorId) return null
    const res = await apiFetch(`/doctors/${doctorId}/agreement`)
    return (res && res.agreement) || null
  },
  casePdf: async (caseId) => apiFetch(`/cases/${caseId}/practice-agreement/pdf`),
}
